import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { debounce } from 'lodash';
import { ClipLoader } from 'react-spinners';
import { useParams, useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import ToolBar from '../../components/toolbar/ToolBar.tsx';
import type { TextTemplate } from '../../components/toolbar/ToolBar.tsx';
import Timeline from '../../components/timeline/Timeline.tsx';
import AssetMenu from '../../components/assetmenu/AssetMenu.tsx';
import './home.css';
import PixiCanvas from '../../components/canvas/PixiCanvas.tsx';
import Header from '../../components/header/Header.tsx';
import { Application, Graphics, Sprite } from 'pixi.js';
import ablyClient from './../../config/ablyClient';
import { TimelineState } from '@xzdarcy/react-timeline-editor';
import {
  GeneratedAudioResult,
  handleAudioGeneration,
} from '../../services/aiAudioService.ts';
import { handleBackgroundGeneration } from '../../services/aiBackgroundService.ts';
import {
  InsufficientPixlesError,
  NoActiveEntitlementError,
} from '../../services/entitlementErrors.ts';
import { getAudioBlob } from '../../services/canvasAudioBlobService.ts';
import {
  getStitchedVideoBlob,
  StitchTimeoutError,
} from '../../services/canvasVideoBlobService.ts';
import {
  newPublishJobId,
  PublishJob,
} from '../../types/publishJob.ts';
import { toast } from 'react-toastify';
import { Close } from '@mui/icons-material';
import { toastMessage } from '../../i18n/toastMessages';
import { getGaAnalyticsPayload } from '../../services/ga4Client';
import {
  AnimationCharacter,
  AssetCharacter,
  AssetType,
  AudioElement,
  CharacterRef,
  Comment,
  CommentDrawingRef,
  Effect,
  FontFamily,
  FontStyle,
  FontWeight,
  Layer,
  Position,
  SpriteWithId,
  TextProps,
  TextRef,
  AudioGenerationRequest,
  BackgroundGenerationRequest,
  BackgroundItem,
  Scene,
  ActionData,
  RiggedCharacterRef,
} from '../../types/typing.ts';
import Comments from '../../components/commentsmenu/CommentsMenu.tsx';
import { getAvatarColor } from '../../components/commentsmenu/avatarColors.ts';
import { BackgroundRecorder } from '../../services/recordingService.ts';
import { cancelBackgroundFetch, canonicalVideoKey, preloadVideo } from '../../components/canvas/utils/videoPreloader.ts';
import { PREFETCH_AHEAD, getPrefetchWindow, toPrefetchItems } from '../../components/canvas/utils/scenePrefetch.ts';
import { handleUploadAudio } from '../../services/audioService.ts';
import {
  handleUploadGeneratedVideo,
  trimGeneratedVideo,
  VideoTrimRange,
} from '../../services/videoStorageService.ts';
import { getAssetDownloadUrl, isSasTokenExpiring, isVideoMediaUrl } from '../../services/assetUrlUtils.ts';
import { resolveCharacterImageUrl } from '../../services/characterStorageService.ts';
import {
  CustomTimelineAction,
  mockData,
  CusTomTimelineRow,
} from '../../components/timeline/mock.ts';
import audioControl from '../../components/timeline/audioControl';
import { cloneDeep } from 'lodash';
import { User, Video, videoService } from '../../services/videoService.ts';
import SceneMenu from '../../components/scenemenu/SceneMenu.tsx';
import {
  decodeName,
  extractTimelineActions,
  hasOnlyVideoScenesAndAudio,
  isGeneratedVideoAction,
  parseTimeToSeconds,
} from '../../components/timeline/timelineUtils.ts';
import CharacterLibrary from '../../components/characterlibrary/CharacterLibrary';
import CharacterVideoGenerator from '../../components/characterlibrary/CharacterVideoGenerator';
import EnvironmentLibrary from '../../components/environmentlibrary/EnvironmentLibrary';
import { CharacterRig } from '../../types/RiggingTypes';
import {
  getEffectDisplayName,
  getEffectListingImage,
  isSunlightEffect,
} from '../../components/canvas/utils/effectPreviews.ts';
import { isCharacterRiggingEnabled } from '../../services/featureFlagsService';

interface HomeProps {
  setBlob: React.Dispatch<React.SetStateAction<Blob | null>>;
  publishJob: PublishJob;
  setPublishJob: React.Dispatch<React.SetStateAction<PublishJob>>;
}

type SasTokenEntry = {
  token?: string;
  expiredAt?: number;
};

const HomePage: React.FC<HomeProps> = ({ setBlob, publishJob, setPublishJob }) => {
  // Latest stitch/record job started from this mount. Async continuations
  // check it before writing job state so a superseded (aborted) job can never
  // overwrite the newer job's status — and so a stale continuation from a
  // previous mount stays silent after remount.
  const publishJobIdRef = useRef<string>('');
  const { id: videoId } = useParams();
  const location = useLocation();
  const [loading, setLoading] = useState<boolean>(true);

  const [title, setTitle] = useState<string | undefined>(
    location.state?.title || 'Untitled'
  );
  const [ownedBy, setOwnedBy] = useState<User | undefined>(undefined);
  const [sharedWith, setSharedWith] = useState<User[]>([]);
  const [sasByVideoId, setSasByVideoId] = useState<
    Record<string, SasTokenEntry>
  >({});

  // Holds the most recently known video document so relative blob paths can be
  // resolved into playable URLs using its SAS token.
  const currentVideoRef = useRef<Video | null>(null);

  const [backgroundImage, setBackgroundImageState] = useState<any>(null);
  const activeVideoSas = videoId ? sasByVideoId[videoId] : undefined;

  // Resolve relative blob paths against the current video's SAS token before
  // pushing them to the canvas. This is the single funnel for ALL background
  // updates (handlers, the timeline's backgroundControl via action `ref`, etc.)
  // so a stored relative path can never leak into the renderer un-signed.
  const setBackgroundImage = useCallback(
    (imageData: any, sasTokenOverride?: string) => {
      const sasToken =
        sasTokenOverride ||
        activeVideoSas?.token ||
        currentVideoRef.current?.sas?.token;
      if (typeof imageData === 'string') {
        setBackgroundImageState(getAssetDownloadUrl(imageData, sasToken));
        return;
      }
      if (imageData && typeof imageData.src === 'string') {
        setBackgroundImageState({
          ...imageData,
          src: getAssetDownloadUrl(imageData.src, sasToken),
        });
        return;
      }
      setBackgroundImageState(imageData);
    },
    [activeVideoSas?.token]
  );

  const cacheVideoSas = useCallback(
    (id: string | undefined, sas?: Video['sas']) => {
      if (!id || typeof sas?.token !== 'string' || !sas.token) return;

      setSasByVideoId((prev) => {
        const nextEntry: SasTokenEntry = {
          token: sas.token,
          expiredAt: sas.expiredAt,
        };
        const existingEntry = prev[id];

        if (
          existingEntry?.token === nextEntry.token &&
          existingEntry?.expiredAt === nextEntry.expiredAt
        ) {
          return prev;
        }

        return {
          ...prev,
          [id]: nextEntry,
        };
      });
    },
    []
  );

  // Keep the timeline's audio playback resolver in sync with the current
  // video's SAS token (mirrors the canvas background resolution funnel).
  useEffect(() => {
    audioControl.setSasToken(
      activeVideoSas?.token ?? currentVideoRef.current?.sas?.token
    );
  }, [activeVideoSas?.token]);

  // Rotate the current video's SAS token before it expires. Scene thumbnails,
  // the canvas background and audio all resolve relative blob paths with this
  // token at render time, so a long-lived session must refresh it — otherwise
  // every signed URL starts returning 403 (blank scene thumbnails, dead
  // playback) even though the underlying blobs are intact.
  useEffect(() => {
    if (!videoId) return;

    const refreshSasIfNeeded = () => {
      const entry = sasByVideoId[videoId];
      // Only act on tokens whose expiry is known; a token without `expiredAt`
      // cannot be checked and refreshing it blindly would poll the backend.
      if (!entry?.token || typeof entry.expiredAt !== 'number') return;
      if (!isSasTokenExpiring({ sas: entry })) return;

      videoService
        .getVideoById(videoId)
        .then((video) => {
          currentVideoRef.current = video;
          cacheVideoSas(videoId, video?.sas);
        })
        .catch((error) => {
          console.error('Failed to refresh video SAS token:', error);
        });
    };

    refreshSasIfNeeded();
    const interval = window.setInterval(refreshSasIfNeeded, 60 * 1000);
    return () => window.clearInterval(interval);
  }, [videoId, sasByVideoId, cacheVideoSas]);

  const [selectedBackgrounds, setSelectedBackgrounds] = useState<
    BackgroundItem[]
  >([]);

  const defaultEditorData = cloneDeep(mockData);
  const [timelineData, setTimelineData] = useState<CusTomTimelineRow[]>([
    {
      id: 'background-row',
      name: 'Backgrounds',
      start: 0,
      end: 10,
      actions: [
        {
          id: 'background-action-1',
          start: 0,
          end: 10,
          effectId: 'effect4',
          data: {
            src: '1',
            name: 'Scene 1',
            ref: setBackgroundImage,
            imageData: null,
          },
        },
      ],
    },
    ...defaultEditorData.slice(1),
  ]);
  const navMenuRef = useRef<HTMLDivElement | null>(null);
  const assetmenuRef = useRef<HTMLDivElement>(null);
  const [selectedAudio, setSelectedAudio] = useState<AudioElement[]>([]);
  const appRef = useRef<Application | null>(null);
  const [selectedAnimation, setSelectedAnimation] = useState<
    AnimationCharacter[]
  >([]);
  const [audioFiles, setAudioFiles] = useState<any[]>([]);
  const [selectedEffect, setSelectedEffect] = useState<Effect[]>([]);
  const [selectedUploadedAssets, setSelectedUploadedAssets] = useState<
    AssetCharacter[]
  >([]);
  const [selectedTexts, setSelectedTexts] = useState<TextProps[]>([]);

  const [assetMenuVisible, setAssetMenuVisible] = useState(false);
  const [layers, setLayers] = useState<Layer[]>([]);
  const [selectedAssetOnCanvas, setSelectedAssetOnCanvas] = useState<{
    id: string;
    selectedAsset: AssetType;
  }>({ id: '', selectedAsset: AssetType.NONE });

  const [selectedMenu, setSelectedMenu] = useState<string | null>();
  const charactersRef = useRef<Map<string, CharacterRef>>(new Map());
  const commentContainerRef = useRef<Map<string, CommentDrawingRef>>(new Map());
  const textRef = useRef<Map<string, TextRef>>(new Map());
  const effectsSpriteRef = useRef<SpriteWithId | null>(null);
  const backgroundImageRef = useRef<Sprite | null>(null);
  const drawingRef = useRef<Graphics | null>(null);

  const [isRecording, setIsRecording] = useState(false);
  const [recordingStartTime, setRecordingStartTime] = useState<number | null>(
    null
  );
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);
  const [selectedCommentId, setSelectedCommentId] = useState<string | null>(
    null
  );
  const [commentDrawingPoints, setCommentDrawingPoints] = useState<
    Position[] | null
  >([]);
  // Mirrors commentDrawingPoints so we can detect the start of a new drawing
  // stroke without relying on stale closure state.
  const commentDrawingPointsRef = useRef<Position[]>([]);
  // Timeline position that was active while the pencil comment was being drawn,
  // used when the drawing is saved so it lines up with comments in the section.
  const [commentDrawingTimestamp, setCommentDrawingTimestamp] = useState(0);
  const [lastSelectedCharacterIndex, setLastSelectedCharacterIndex] = useState<
    string | null
  >(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [isPublishing, setIsPublishing] = useState(false);
  const recorderRef = useRef<BackgroundRecorder | null>(null);
  const timelineState = useRef<TimelineState | null>(null);
  // True once the canvas app + timeline engine exist and the recorder is
  // built. Drives the Publish button state so the first click can never hit
  // a half-initialized editor.
  const [editorReady, setEditorReady] = useState(false);

  // One-shot: lifts the boot overlay the first time the canvas background
  // (first scene) reports painted. When the first scene holds a video, the
  // page would otherwise look loaded while the canvas sits blank for the
  // seconds the video needs. Never reset — later background swaps must not
  // re-block the page.
  const [initialCanvasReady, setInitialCanvasReady] = useState(false);
  const initialCanvasReadyRef = useRef(false);
  const handleInitialCanvasReady = useCallback(() => {
    if (initialCanvasReadyRef.current) return;
    initialCanvasReadyRef.current = true;
    setInitialCanvasReady(true);
  }, []);

  // Backstop: never trap the user behind the boot overlay if the first
  // background stalls (broken URL, stalled network) and never reports ready.
  useEffect(() => {
    if (loading || initialCanvasReady) return;
    const timer = window.setTimeout(() => handleInitialCanvasReady(), 20000);
    return () => window.clearTimeout(timer);
  }, [loading, initialCanvasReady, handleInitialCanvasReady]);

  /**
   * Creates the recorder on demand if its inputs exist. Ref assignments by
   * the canvas/timeline children never re-render Home, so recorder setup
   * cannot rely on effect timing alone — this is also called from
   * handlePublish as a final guarantee. Returns whether publish can proceed.
   */
  const ensureRecorderReady = useCallback((): boolean => {
    const currentApp = appRef.current;
    if (
      currentApp &&
      timelineState.current &&
      recorderRef.current &&
      !recorderRef.current.usesApp(currentApp)
    ) {
      // A canvas remount creates a new Pixi app; never record against the old
      // (possibly destroyed) instance.
      recorderRef.current = new BackgroundRecorder(
        currentApp,
        timelineState.current
      );
    }
    if (!recorderRef.current && currentApp && timelineState.current) {
      recorderRef.current = new BackgroundRecorder(
        currentApp,
        timelineState.current
      );
    }
    const ready = Boolean(recorderRef.current && timelineState.current);
    if (ready) {
      setEditorReady((prev) => (prev ? prev : true));
    }
    return ready;
  }, []);
  const canvasWrapperRef = useRef<HTMLDivElement | null>(null);
  const [canvasViewport, setCanvasViewport] = useState(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : 1280,
    height: typeof window !== 'undefined' ? window.innerHeight : 720,
  }));
  // Phones only: nudge portrait holders to rotate. Dismissible per session;
  // the editor stays mounted behind the prompt so Pixi/timeline state is kept.
  const PORTRAIT_PHONE_QUERY =
    '(orientation: portrait) and (max-width: 900px) and (pointer: coarse)';
  const [isPortraitPhone, setIsPortraitPhone] = useState(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia !== 'undefined' &&
      window.matchMedia(PORTRAIT_PHONE_QUERY).matches
  );
  const [rotateDismissed, setRotateDismissed] = useState(false);
  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      typeof window.matchMedia === 'undefined'
    )
      return;
    const mql = window.matchMedia(PORTRAIT_PHONE_QUERY);
    const handleChange = (event: MediaQueryListEvent) => {
      setIsPortraitPhone(event.matches);
      // Re-show the prompt on a fresh portrait entry (e.g. rotated back).
      setRotateDismissed(false);
    };
    setIsPortraitPhone(mql.matches);
    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, []);
  // Pause playback under the prompt so audio doesn't play behind it.
  useEffect(() => {
    if (isPortraitPhone && !rotateDismissed) {
      timelineState.current?.pause();
    }
  }, [isPortraitPhone, rotateDismissed]);

  const navigate = useNavigate();
  //to-do: Move key to backend
  const channel = ablyClient.channels.get(`pixle-pro-collab-${videoId}`);
  const modules = import.meta.glob('../../assets/animations/characters/*.json');
  //const scene1Id = crypto.randomUUID();
  const scene1Id = '1';

  // Character Library state
  const [isCharacterLibraryOpen, setIsCharacterLibraryOpen] = useState(false);
  const [isCharacterVideoGeneratorOpen, setIsCharacterVideoGeneratorOpen] =
    useState(false);
  const [characterVideoMode, setCharacterVideoMode] = useState<
    'character' | 'liveAction'
  >('character');
  // True while the video generator holds an unsaved generated video, so
  // overlay/header closes confirm before discarding it.
  const [isCharacterVideoUnsaved, setIsCharacterVideoUnsaved] = useState(false);

  const requestCloseCharacterVideoGenerator = () => {
    if (
      isCharacterVideoUnsaved &&
      !window.confirm(toastMessage('characterVideo.discardVideoConfirm'))
    ) {
      return;
    }
    setIsCharacterVideoUnsaved(false);
    setIsCharacterVideoGeneratorOpen(false);
  };
  // When the video generator hands off to the Character Studio, the studio
  // focuses directly on the selected character so the user can edit it.
  const [studioFocusCharacterId, setStudioFocusCharacterId] = useState<
    string | undefined
  >(undefined);
  // Bumped whenever the Character Studio closes so the video generator (which
  // stays open underneath) can refresh its cached library cards after edits.
  const [characterLibraryRevision, setCharacterLibraryRevision] = useState(0);
  // Environment Library state (first-class reusable locations).
  const [isEnvironmentLibraryOpen, setIsEnvironmentLibraryOpen] =
    useState(false);
  // Bumped whenever the Environment Library closes so the video generator
  // (which stays open underneath) can refresh its environment picker.
  const [environmentLibraryRevision, setEnvironmentLibraryRevision] =
    useState(0);
  const [riggedCharacters, setRiggedCharacters] = useState<
    Array<{
      id: string;
      rig: CharacterRig;
      position: Position;
      scale: number;
      actionId?: string;
    }>
  >([]);
  const riggedCharactersRef = useRef<Map<string, RiggedCharacterRef>>(
    new Map()
  );

  const handleUpdateRiggedCharacter = (
    id: string,
    properties: {
      position?: { x: number; y: number };
      scale?: number;
      pathPoints?: Position[];
    }
  ) => {
    setRiggedCharacters((prev) =>
      prev.map((char) => (char.id === id ? { ...char, ...properties } : char))
    );
  };

  const [scenes, setScenes] = useState<Scene[]>([
    {
      id: scene1Id,
      name: 'Scene 1',
    },
  ]);
  const [selectedSceneId, setSelectedSceneId] = useState<string | null>(
    scene1Id
  );

  // Warm the next scenes' videos ahead of linear playback so crossing into
  // the next scene doesn't stall on a cold fetch. Only the next 2 scenes are
  // ever prefetched (4-6 scenes/video on average, watched linearly): the
  // current scene streams on demand via the renderer, and prefetching
  // everything would split one thin pipe N ways and starve the visible scene.
  // The window is anchored to the *playing* scene (staged background), not the
  // clicked selection — timeline auto-play advances scenes without touching
  // selection, so a selection-anchored window would go stale mid-playback.
  // Prefetches share the preloader's SAS-deduped background downloads;
  // entries that fall out of the window are cancelled while still in flight
  // (warmed blobs and renderer-held entries are never touched).
  const prefetchedKeysRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const sasToken =
      activeVideoSas?.token ?? currentVideoRef.current?.sas?.token;
    const stagedSrc =
      typeof backgroundImage === 'string'
        ? backgroundImage
        : (backgroundImage?.src ?? null);
    const stagedKey =
      typeof stagedSrc === 'string' && stagedSrc
        ? canonicalVideoKey(stagedSrc)
        : null;
    const items = toPrefetchItems(
      scenes,
      (background) => getAssetDownloadUrl(background, sasToken),
      isVideoMediaUrl,
      canonicalVideoKey
    );
    const { wanted, queue } = getPrefetchWindow(
      items,
      stagedKey,
      selectedSceneId,
      PREFETCH_AHEAD
    );
    for (const oldKey of prefetchedKeysRef.current) {
      if (!wanted.includes(oldKey)) cancelBackgroundFetch(oldKey);
    }
    prefetchedKeysRef.current = new Set(wanted);
    // Stagger: nearest scene starts now; the second waits 4s so the visible
    // scene and scene+1 get a head start on slow links. Dedup makes repeats
    // (e.g. SAS refresh re-runs) nearly free.
    if (queue[0]) preloadVideo(queue[0]);
    if (!queue[1]) return;
    const timer = window.setTimeout(() => preloadVideo(queue[1]), 4000);
    return () => window.clearTimeout(timer);
  }, [scenes, selectedSceneId, backgroundImage, activeVideoSas?.token]);

  const getInitialSceneBackgroundImage = (video: Video): string | null => {
    const firstScene = video.scenes?.[0];
    const backgroundRow = Array.isArray(video.timeline)
      ? video.timeline.find(
          (row: { itemId?: string }) => row.itemId === 'background-row'
        )
      : undefined;
    const firstSceneAction = backgroundRow?.actions?.find(
      (action: { id?: string; data?: ActionData }) =>
        firstScene &&
        (action.id === `background-action-${firstScene.id}` ||
          action.data?.src === firstScene.id)
    );
    const firstBackgroundAction =
      firstSceneAction ?? backgroundRow?.actions?.[0];
    const timelineBackgroundImage = firstBackgroundAction?.data?.imageData;

    if (firstScene?.backgroundImage) {
      return firstScene.backgroundImage;
    }

    if (timelineBackgroundImage) {
      return timelineBackgroundImage;
    }

    if ((video.scenes?.length ?? 0) <= 1 && video.backgroundImage) {
      return video.backgroundImage;
    }

    return null;
  };

  const getPersistableLayers = (layersToPersist: Layer[]): Layer[] =>
    layersToPersist.map((layer) => {
      if (
        typeof layer.imageUrl === 'string' &&
        layer.imageUrl.startsWith('data:')
      ) {
        const { imageUrl: _imageUrl, ...layerWithoutInlineImage } = layer;
        return layerWithoutInlineImage;
      }

      return layer;
    });

  const getSceneBaseName = (sceneName: string) =>
    sceneName
      .trim()
      .replace(/\s+\(Copy(?:\s+\d+)?\)$/i, '')
      .trim();

  const getNextSceneName = (existingScenes: Scene[]) => {
    const existingNames = new Set(
      existingScenes.map((scene) => scene.name.trim().toLowerCase())
    );
    const highestSceneNumber = existingScenes.reduce((highest, scene) => {
      const match = getSceneBaseName(scene.name).match(/^Scene\s+(\d+)$/i);
      return match ? Math.max(highest, Number(match[1])) : highest;
    }, 0);

    let nextSceneNumber = highestSceneNumber + 1;
    let nextSceneName = `Scene ${nextSceneNumber}`;

    while (existingNames.has(nextSceneName.toLowerCase())) {
      nextSceneNumber += 1;
      nextSceneName = `Scene ${nextSceneNumber}`;
    }

    return nextSceneName;
  };

  const getDuplicateSceneName = (
    sceneName: string,
    existingScenes: Scene[]
  ) => {
    const baseSceneName = getSceneBaseName(sceneName) || 'Scene';
    const existingNames = new Set(
      existingScenes.map((scene) => scene.name.trim().toLowerCase())
    );
    let copyNumber = 1;
    let nextSceneName = `${baseSceneName} (Copy)`;

    while (existingNames.has(nextSceneName.toLowerCase())) {
      copyNumber += 1;
      nextSceneName = `${baseSceneName} (Copy ${copyNumber})`;
    }

    return nextSceneName;
  };

  const doesSceneHaveContent = (
    sceneAction: CustomTimelineAction,
    data: CusTomTimelineRow[]
  ) => {
    return data.some((row) => {
      if (row.id === 'background-row') {
        // A generated video attached to a background action counts as scene
        // content, so a scene that already has an AI-generated video is not
        // considered empty and will not be overwritten by a later addition.
        return row.actions.some(
          (action) =>
            isGeneratedVideoAction(action) &&
            action.start < sceneAction.end &&
            action.end > sceneAction.start
        );
      }

      return row.actions.some(
        (action) =>
          action.start < sceneAction.end && action.end > sceneAction.start
      );
    });
  };

  const handleAddBackground = (imageData: any) => {
    const newBackground = {
      id: `bg-${Date.now()}`,
      name: 'Background',
      imageData: imageData,
    };

    setSelectedBackgrounds((prev) => [...prev, newBackground]);

    // Create a new scene for the background
    const newScene = {
      id: newBackground.id,
      name: getNextSceneName(scenes),
      backgroundId: newBackground.id,
      backgroundImage: imageData,
      duration: 10, // Set default duration to 10 seconds
    };
    setScenes((prev) => [...prev, newScene]);
  };

  const getRelevantRef = (actionId: string) => {
    switch (true) {
      case actionId.startsWith('animation-action'):
      case actionId.startsWith('animation'):
        return charactersRef;

      case actionId.startsWith('effect-action'):
      case actionId.startsWith('effect'):
        return effectsSpriteRef;

      case actionId.startsWith('background-action'):
        return setBackgroundImage;

      case actionId.startsWith('text-action'):
        return textRef;

      case actionId.startsWith('rig-action'):
      case actionId.startsWith('rig'):
        return riggedCharactersRef;

      default:
        return null;
    }
  };

  const updateActionsFromReceived = (
    existingData: CusTomTimelineRow[],
    receivedActionData: any[]
  ): CusTomTimelineRow[] => {
    if (!Array.isArray(receivedActionData)) return existingData;

    return receivedActionData.map((receivedItem) => {
      const existingItem = existingData.find(
        (item) => item.id === receivedItem.itemId
      );

      const newActions: CustomTimelineAction[] = (
        receivedItem.actions ?? []
      ).map(
        (receivedAction: {
          id: string;
          start: number;
          end: number;
          effectId: string;
          data: any;
        }) => {
          const existingAction = existingItem?.actions.find(
            (action) => action.id === receivedAction.id
          );
          const relevantRef =
            existingAction?.data.ref ?? getRelevantRef(receivedAction.id);

          return {
            ...existingAction,
            id: receivedAction.id,
            data: {
              ...receivedAction.data,
              ref: relevantRef,
            },
            start: receivedAction.start,
            end: receivedAction.end,
            effectId: receivedAction.effectId,
          };
        }
      );

      return {
        ...existingItem,
        end:
          newActions.length > 0
            ? Math.max(...newActions.map((action) => action.end))
            : (existingItem?.end ?? 0),
        src: existingItem?.src ?? '',
        start:
          newActions.length > 0
            ? Math.min(...newActions.map((action) => action.start))
            : (existingItem?.start ?? 0),
        id: receivedItem.itemId,
        name: receivedItem.itemName,
        actions: newActions,
      };
    });
  };

  const clearAllRefs = () => {
    commentContainerRef.current.clear();
    charactersRef.current.clear();
    appRef.current = null;
    textRef.current.clear();
    effectsSpriteRef.current = null;
    drawingRef.current = null;
    backgroundImageRef.current = null;
  };

  useEffect(() => {
    return () => {
      // Leaving the editor mid-play must silence everything: pause the engine
      // (drives canvas video playback) and stop all timeline audio (looped
      // SFX + player track keep sounding on their own otherwise). Canvas
      // video elements are released by ImageRenderer's own unmount teardown.
      try {
        timelineState.current?.pause();
      } catch {
        // Best-effort teardown must never break unmount.
      }
      try {
        audioControl.stopAll();
      } catch {
        // Best-effort teardown must never break unmount.
      }
      clearAllRefs(); // Reset refs when unmounting
    };
  }, []);

  useEffect(() => {
    const fetchVideo = async () => {
      try {
        if (videoId) {
          setLoading(true);

          const video = await videoService.getVideoById(videoId);
          currentVideoRef.current = video;
          cacheVideoSas(videoId, video?.sas);
          setOwnedBy(video.ownedBy as User);

          setSharedWith((video.sharedWith as User[]) || []);

          setTitle(video.title);

          if (video.scenes?.length > 0) {
            setScenes(video.scenes);
            setSelectedSceneId(video.scenes[0].id);
          }

          if (video.audio) {
            setSelectedAudio(video.audio);
          }

          const initialBackgroundImage = getInitialSceneBackgroundImage(video);
          setBackgroundImage(initialBackgroundImage, video.sas?.token);

          if (video.texts) {
            setSelectedTexts(video.texts);
          }

          if (video.animations) {
            const updatedAnimations = await Promise.all(
              video.animations.map(async (animation: AnimationCharacter) => {
                if (!animation.animation) {
                  animation.animation = await loadAnimation(
                    animation.name.trim()
                  );
                }
                return animation;
              })
            );
            setSelectedAnimation(updatedAnimations);
          }

          if (video.effects) {
            setSelectedEffect(video.effects);
          }

          if (video.comments) {
            setComments(video.comments);
          }

          if (video.layers) {
            setLayers(video.layers);
          }

          if (video.timeline) {
            const receivedActionData = video.timeline;
            setTimelineData((currentData) =>
              updateActionsFromReceived(currentData, receivedActionData)
            );
          }
        }
      } catch (error) {
        console.error('Error fetching video:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVideo();
  }, [videoId]);

  const loadAnimation = async (animationName: string) => {
    const modulePath = `../../assets/animations/characters/${animationName}.json`;

    if (modules[modulePath]) {
      const response = await modules[modulePath]();
      // @ts-ignore
      return response.default;
    } else {
      console.error(`Animation file not found: ${modulePath}`);
      return null;
    }
  };

  useEffect(() => {
    channel.subscribe(function (message) {
      switch (message.name) {
        case 'handleBackgroundChange':
          handleAddBackground(message.data);
          break;
        case 'handleAnimationChange':
          Promise.all(
            message.data.map(async (animation: AnimationCharacter) => {
              try {
                if (!animation.animation) {
                  animation.animation = await loadAnimation(
                    animation.name.trim()
                  );
                  setSelectedAnimation([animation]); // Directly update state
                }
              } catch (error) {
                console.error('Error loading animation:', error);
              }
            })
          ).then(() => {
            setSelectedAnimation([...message.data]);
          });
          break;
        case 'handleEffectChange':
          setSelectedEffect(message.data);
          break;
        case 'handleTextUpdate':
          setSelectedTexts(message.data);
          break;
        case 'handleTextSelect':
          setSelectedTexts(message.data);
          break;
        case 'handleAudioUpdate':
          setSelectedAudio(message.data);
          break;
        case 'handleFileNameUpdate':
          setTitle(message.data);
          break;
        case 'handleTimelineEdit':
          const receivedActionData = message.data;
          setTimelineData((currentData) =>
            updateActionsFromReceived(currentData, receivedActionData)
          );
          break;
        case 'handleScenesUpdate':
          // Scene order / durations / add-delete were previously
          // published but never received — collaborators stayed stale.
          setScenes(message.data as Scene[]);
          break;
        case 'handleCommentUpdate':
          setComments(message.data);
          break;
        case 'handleLayersUpdate':
          setLayers(message.data);
          break;
        default:
          break;
      }
    });
    return () => {
      channel.unsubscribe();
    };
  }, []);
  useEffect(() => {
    setLayers((prevLayers: Layer[]) => {
      const newLayers = prevLayers.map((layer) => {
        if (layer.type === AssetType.TEXT) {
          const matchingText = selectedTexts.find(
            (text) => text.id === layer.id
          );
          return {
            ...layer,
            name: matchingText?.text ?? 'Default Text',
          };
        }
        return layer;
      });
      /*if(videoId){
        videoService.patchVideo(videoId, {
          layers: getPersistableLayers(newLayers),
        });
      }*/
      return newLayers;
    });
  }, [selectedTexts]);

  // Recorder inputs land asynchronously without re-rendering Home, and
  // refs in a dep array never re-fire — so poll until both exist, then stop.
  // (Refs in deps was the original bug: first Publish could fire before the
  // recorder existed.)
  useEffect(() => {
    if (ensureRecorderReady()) return;
    const interval = window.setInterval(() => {
      if (ensureRecorderReady()) window.clearInterval(interval);
    }, 500);
    return () => window.clearInterval(interval);
  }, [ensureRecorderReady]);

  useEffect(() => {
    const element = canvasWrapperRef.current;
    if (!element) return;

    const updateCanvasViewport = () => {
      const { width, height } = element.getBoundingClientRect();
      const nextViewport = {
        width: Math.floor(width),
        height: Math.floor(height),
      };

      setCanvasViewport((currentViewport) =>
        currentViewport.width === nextViewport.width &&
        currentViewport.height === nextViewport.height
          ? currentViewport
          : nextViewport
      );
    };

    updateCanvasViewport();

    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(updateCanvasViewport)
        : null;

    resizeObserver?.observe(element);
    window.addEventListener('resize', updateCanvasViewport);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateCanvasViewport);
    };
  }, [loading]);

  useEffect(() => {
    if (drawingRef.current && !isCommentsOpen) {
      drawingRef.current.destroy();
    }
    return () => {
      drawingRef.current = null;
    };
  }, [isCommentsOpen]);

  const hasStitchableMedia = (data: CusTomTimelineRow[]): boolean =>
    data.some((segment) =>
      segment.actions.some((action) => {
        if (action.id.startsWith('audio-action')) return true;
        return isGeneratedVideoAction(action);
      })
    );

  const calculateDuration = (data: CusTomTimelineRow[]): number => {
    let duration = 0;
    data.forEach((row) => {
      row.actions.forEach((action) => {
        if (action.end > duration) {
          duration = action.end;
        }
      });
    });
    return duration;
  };

  const handlePublish = async () => {
    // Loud guard: every dead end here tells the user why instead of silently
    // swallowing the click. ensureRecorderReady is the final guarantee, so
    // this toast is now a backstop for a genuinely still-loading editor.
    if (!ensureRecorderReady()) {
      toast.error(toastMessage('editor.stillLoading'));
      return;
    }
    if (isPublishing || publishJob.status === 'working') {
      toast.info(toastMessage('publish.inProgress'));
      return;
    }
    const recorder = recorderRef.current;
    if (!recorder) {
      toast.error(toastMessage('editor.stillLoading'));
      return;
    }
    try {
      setIsPublishing(true);
      const duration = calculateDuration(timelineData);
      const serializableTimelineData = extractTimelineActions(timelineData);
      const token = localStorage.getItem('authToken');

      const navigationState = {
        timelineData: serializableTimelineData,
        title: title,
        error: null,
      };

      let videoBlob = null;
      let recordedExport: { jobId: string; format: 'mp4' | 'webm' } | null =
        null;

      // When the video is purely scenes backed by generated videos (+ optional
      // audio) with no text, characters or props drawn on the canvas, we can
      // skip canvas recording entirely and let the backend stitch the clips.
      const canStitchVideoDirectly =
        hasOnlyVideoScenesAndAudio(timelineData) &&
        selectedUploadedAssets.length === 0;

      if (canStitchVideoDirectly) {
        // Stitch path (primary): navigate instantly for feedback, then run
        // the stitch as a single-flight job. Completion/failure is delivered
        // through App-level job state — never a fake timer.
        const jobId = newPublishJobId();
        publishJobIdRef.current = jobId;
        setPublishJob({ status: 'working', stage: 'sending', error: null, jobId });
        navigate('/publish', {
          state: { ...navigationState, preparing: true, jobId },
        });

        try {
          // Get the stitched video in the background
          const analytics = await getGaAnalyticsPayload();
          const stitchResult = await getStitchedVideoBlob(
            timelineData,
            videoId,
            token,
            {
              ...(analytics
                ? { analytics: { ...analytics, eventId: jobId } }
                : {}),
              onStage: (stage) => {
                if (publishJobIdRef.current !== jobId) return;
                setPublishJob({ status: 'working', stage, error: null, jobId });
              },
            }
          );
          videoBlob = stitchResult.blob;
        } catch (stitchError) {
          // A superseded job (re-publish aborted this one, or a stale
          // continuation from a previous mount) must not touch job state —
          // the newer job owns it.
          if (publishJobIdRef.current !== jobId) return;
          const message =
            stitchError instanceof StitchTimeoutError
              ? 'Video stitching timed out. Please try again.'
              : stitchError instanceof Error
                ? stitchError.message
                : 'Failed to stitch the video. Please try again.';
          // Stay put: the already-mounted Publish page flips to this error
          // inline instead of bouncing through /recording-error.
          setPublishJob({ status: 'error', stage: null, error: message, jobId });
          setIsPublishing(false);
          return;
        }
      } else {
        // Record fallback path: unchanged navigate-first behavior, plus job
        // status so the header and Publish page stay honest.
        const jobId = newPublishJobId();
        publishJobIdRef.current = jobId;
        setPublishJob({ status: 'working', stage: 'recording', error: null, jobId });
        recorder.prepareForRouteCapture();
        navigate('/publish', { state: { ...navigationState, preparing: true, jobId } });

        let recordedBlob: Blob;
        if (hasStitchableMedia(timelineData)) {
          const audioBlob = await getAudioBlob(timelineData, videoId, token);
          recordedBlob = await recorder.recordInBackground(
            duration,
            audioBlob,
            timelineData
          );
        } else {
          recordedBlob = await recorder.recordInBackground(duration);
        }
        videoBlob = recordedBlob;
        recordedExport = {
          jobId,
          format: recordedBlob.type.includes('webm') ? 'webm' : 'mp4',
        };
      }
      setBlob(videoBlob);
      if (publishJobIdRef.current) {
        setPublishJob({
          status: 'ready',
          stage: null,
          error: null,
          jobId: publishJobIdRef.current,
        });
      }

      // The stitch path reports from the backend after the output is uploaded.
      // The canvas-recording fallback reports here once the blob is ready.
      if (recordedExport && videoId) {
        const { jobId, format } = recordedExport;
        void getGaAnalyticsPayload()
          .then((analytics) =>
            analytics
              ? videoService.recordVideoExport(videoId, jobId, format, {
                  ...analytics,
                  eventId: jobId,
                })
              : undefined
          )
          .catch((exportAnalyticsError) => {
            console.error(
              'Failed to record video export analytics:',
              exportAnalyticsError
            );
          });
      }
    } catch (error) {
      recorderRef.current?.cancelRouteCapture();
      console.error('Publishing failed:', error);
      const message =
        error instanceof Error ? error.message : 'An error occurred while publishing';
      if (publishJobIdRef.current) {
        setPublishJob({
          status: 'error',
          stage: null,
          error: message,
          jobId: publishJobIdRef.current,
        });
      }
      navigate('/recording-error', {
        state: {
          error: message,
        },
      });
    } finally {
      setIsPublishing(false);
    }
  };

  const handleTextSelect = (template?: TextTemplate): void => {
    if (!selectedSceneId) {
      toast.error(toastMessage('scene.selectFirst'));
      return;
    }

    const defaultTextProps: TextProps = {
      id: crypto.randomUUID(),
      text: template?.text ?? '',
      position: { x: 100, y: 100 },
      scale: 1,
      boxWidth: 440,
      styleType: template?.styleType ?? 'default',
      fontProperties: {
        fontFamily: template?.fontProperties.fontFamily ?? FontFamily.Arial,
        fontSize: template?.fontProperties.fontSize ?? 36,
        fontColor: template?.fontProperties.fontColor ?? '#000000',
        fontStyle: template?.fontProperties.fontStyle ?? FontStyle.Normal,
        fontWeight: template?.fontProperties.fontWeight ?? FontWeight.Normal,
      },
    };

    // Find the scene's time range from the background row
    const backgroundRow = timelineData.find(
      (row) => row.id === 'background-row'
    );
    const sceneAction = backgroundRow?.actions.find(
      (action) => action.id === `background-action-${selectedSceneId}`
    );

    if (!sceneAction) {
      toast.error(toastMessage('scene.notFound'));
      return;
    }

    // Create new row for the text
    const newTextRow: CusTomTimelineRow = {
      id: defaultTextProps.id,
      name: defaultTextProps.text || 'Enter text here',
      start: sceneAction.start,
      end: sceneAction.end,
      actions: [
        {
          id: 'text-action-' + defaultTextProps.id,
          start: sceneAction.start,
          end: sceneAction.end,
          effectId: 'effect3',
          data: {
            src: defaultTextProps.id,
            name: defaultTextProps.text || 'Enter text here',
            ref: textRef,
            sceneId: selectedSceneId,
          } as ActionData,
        },
      ],
    };

    // Update timeline data
    setTimelineData((prevData) => {
      // Ensure background row is first
      const backgroundRow = prevData.find((row) => row.id === 'background-row');
      if (!backgroundRow) return prevData;

      // Create new array with background row first
      const newData = [backgroundRow];

      // Add all other rows except background
      const otherRows = prevData.filter((row) => row.id !== 'background-row');

      // Add all other rows
      newData.push(...otherRows);

      // Add the new text row
      newData.push(newTextRow);

      const actionData = extractTimelineActions(newData);
      channel.publish('handleTimelineEdit', actionData);

      if (videoId) {
        videoService.patchVideo(videoId, { timeline: actionData });
      }

      return newData;
    });

    // Update text state by appending the new text
    setSelectedTexts((prevTexts: TextProps[]) => {
      const newTexts = [...prevTexts, defaultTextProps];
      channel.publish('handleTextSelect', newTexts);
      if (videoId) {
        videoService.patchVideo(videoId, { texts: newTexts });
      }
      return newTexts;
    });

    // Update layers
    setLayers((prevLayers) => {
      const newLayers = [
        ...prevLayers,
        {
          id: defaultTextProps.id,
          name: defaultTextProps.text,
          type: AssetType.TEXT,
          visible: true,
          locked: false,
        },
      ];
      return newLayers;
    });
  };

  const getPlayableAssetUrl = (assetUrl: string): string =>
    getAssetDownloadUrl(
      assetUrl,
      activeVideoSas?.token ?? currentVideoRef.current?.sas?.token
    );

  const getMediaDuration = (
    sourceUrl: string,
    tagName: 'audio' | 'video'
  ): Promise<number> =>
    new Promise((resolve, reject) => {
      const element = document.createElement(tagName);
      const cleanup = () => {
        element.removeEventListener('loadedmetadata', handleLoadedMetadata);
        element.removeEventListener('error', handleError);
        element.src = '';
      };
      const handleLoadedMetadata = () => {
        const duration = Number.isFinite(element.duration) ? element.duration : 0;
        cleanup();
        resolve(duration);
      };
      const handleError = () => {
        cleanup();
        reject(new Error(`Failed to load ${tagName} metadata`));
      };

      element.preload = 'metadata';
      element.addEventListener('loadedmetadata', handleLoadedMetadata);
      element.addEventListener('error', handleError);
      element.src = sourceUrl;
      element.load();
    });

  const addAudioToTimeline = async (
    audioUrl: string,
    displayName?: string,
    providedDuration?: number
  ): Promise<void> => {
    const newAudio: AudioElement = {
      id: crypto.randomUUID(),
      url: audioUrl,
    };

    // Find the scene's time range from the background row
    const backgroundRow = timelineData.find(
      (row) => row.id === 'background-row'
    );
    const sceneAction = backgroundRow?.actions.find(
      (action) => action.id === `background-action-${selectedSceneId}`
    );

    if (!sceneAction) {
      toast.error(toastMessage('scene.notFound'));
      return;
    }

    // Prefer a known duration (e.g. supplied by the AI generation API) so the
    // clip can be sized without loading the (SAS-signed) stored blob. When no
    // duration is known, probe the playable URL but fall back to a sane default
    // so a transient metadata failure never blocks adding the audio.
    let duration: number;
    if (typeof providedDuration === 'number' && providedDuration > 0) {
      duration = providedDuration;
    } else {
      const playableAudioUrl = getPlayableAssetUrl(audioUrl);
      duration = await getMediaDuration(playableAudioUrl, 'audio').catch(
        () => {
          console.warn(
            'Failed to load audio metadata; using fallback duration for',
            audioUrl
          );
          return 10;
        }
      );
    }
    const insertTime = timelineState.current?.getTime() || sceneAction.start;
    const audioName = displayName || decodeName(audioUrl);

    const newAudioRow: CusTomTimelineRow = {
      id: newAudio.id,
      name: audioName,
      start: insertTime,
      end: insertTime + duration,
      actions: [
        {
          id: 'audio-action-' + newAudio.id,
          start: insertTime,
          end: insertTime + duration,
          effectId: 'effect0',
          data: {
            src: audioUrl,
            name: audioName,
            ref: null,
            sceneId: selectedSceneId,
          } as ActionData,
        },
      ],
    };

    setTimelineData((prevData) => {
      const backgroundRow = prevData.find((row) => row.id === 'background-row');
      if (!backgroundRow) return prevData;

      const newData = [backgroundRow];
      const otherRows = prevData.filter((row) => row.id !== 'background-row');
      newData.push(...otherRows, newAudioRow);

      const actionData = extractTimelineActions(newData);
      channel.publish('handleTimelineEdit', actionData);

      if (videoId) {
        videoService.patchVideo(videoId, { timeline: actionData });
      }
      return newData;
    });

    setSelectedAudio((prevAudio) => {
      const updatedAudio = [...prevAudio, newAudio];
      channel.publish('handleAudioUpdate', updatedAudio);
      if (videoId) {
        videoService.patchVideo(videoId, { audio: updatedAudio });
      }
      console.log('updatedAudio', updatedAudio);
      return updatedAudio;
    });

    setLayers((prevLayers) => [
      ...prevLayers,
      {
        id: newAudio.id,
        name: 'Audio Layer',
        type: AssetType.AUDIO,
        visible: true,
        locked: false,
      },
    ]);
  };

  const handleAudioSelect = (audioUrl: string, fileName?: string): void => {
    addAudioToTimeline(audioUrl, fileName).catch((error) => {
      console.error('Failed to add audio to timeline:', error);
      toast.error(toastMessage('home.audioAddFailed'));
    });
  };

  const handleEffectSelect = (effect: string): void => {
    if (!selectedSceneId) {
      toast.error(toastMessage('scene.selectFirst'));
      return;
    }

    const newEffect: Effect = {
      id: crypto.randomUUID(),
      type: effect,
    };
    const effectDisplayName = getEffectDisplayName(newEffect.type);

    setSelectedEffect((prevEffects) => {
      const updatedEffects = [...prevEffects, newEffect];
      channel.publish('handleEffectChange', updatedEffects);
      if (videoId) {
        videoService.patchVideo(videoId, { effects: updatedEffects });
      }
      return updatedEffects;
    });

    // Find the scene's time range from the background row
    const backgroundRow = timelineData.find(
      (row) => row.id === 'background-row'
    );
    const sceneAction = backgroundRow?.actions.find(
      (action) => action.id === `background-action-${selectedSceneId}`
    );

    if (!sceneAction) {
      toast.error(toastMessage('scene.notFound'));
      return;
    }

    // Create new row for the effect
    const newEffectRow: CusTomTimelineRow = {
      id: newEffect.id,
      name: `${effectDisplayName} Effect`,
      start: sceneAction.start,
      end: sceneAction.end,
      actions: [
        {
          id: 'effect-action-' + newEffect.id,
          start: sceneAction.start,
          end: sceneAction.end,
          effectId: 'effect2',
          data: {
            src: newEffect.id,
            name: effectDisplayName,
            ref: effectsSpriteRef,
            sceneId: selectedSceneId,
          } as ActionData,
        },
      ],
    };

    // Update timeline data
    setTimelineData((prevData) => {
      // Ensure background row is first
      const backgroundRow = prevData.find((row) => row.id === 'background-row');
      if (!backgroundRow) return prevData;

      // Create new array with background row first
      const newData = [backgroundRow];

      // Add all other rows except background
      const otherRows = prevData.filter((row) => row.id !== 'background-row');

      // Add all other rows
      newData.push(...otherRows);

      // Add the new effect row
      newData.push(newEffectRow);

      const actionData = extractTimelineActions(newData);
      channel.publish('handleTimelineEdit', actionData);

      if (videoId) {
        videoService.patchVideo(videoId, { timeline: actionData });
      }

      return newData;
    });

    // Update layers
    setLayers((prevLayers) => {
      const newEffectLayer: Layer = {
        id: newEffect.id,
        name: `${effectDisplayName} Effect Layer`,
        type: AssetType.EFFECT,
        visible: true,
        locked: false,
        imageUrl: getEffectListingImage(newEffect.type),
      };

      if (!isSunlightEffect(newEffect.type)) {
        return [...prevLayers, newEffectLayer];
      }

      const backgroundLayerIndex = prevLayers.reduce(
        (lastIndex, layer, index) =>
          layer.type === AssetType.BACKGROUND ? index : lastIndex,
        -1
      );
      const newLayers = [...prevLayers];
      newLayers.splice(
        backgroundLayerIndex >= 0 ? backgroundLayerIndex + 1 : 0,
        0,
        newEffectLayer
      );
      return newLayers;
    });

    // Publish the effect change with all effects
    channel.publish('handleEffectChange', [...selectedEffect, newEffect]);
  };

  const handleLayerDrag = (draggedIndex: number, droppedIndex: number) => {
    const updatedLayers = Array.from(layers);
    const [movedLayer] = updatedLayers.splice(draggedIndex, 1);
    updatedLayers.splice(droppedIndex, 0, movedLayer);
    setLayers(updatedLayers);
    if (videoId) {
      channel.publish('handleLayersUpdate', updatedLayers);
      videoService.patchVideo(videoId, {
        layers: getPersistableLayers(updatedLayers),
      });
    }
  };

  const handleLayerLock = (index: number) => {
    const updatedLayers = [...layers];
    updatedLayers[index].locked = !updatedLayers[index].locked;
    setLayers(updatedLayers);
  };

  const handleMenuClick = (menu: string) => {
    if (menu === 'riggedCharacters') {
      setIsCharacterLibraryOpen(true);
    } else {
      setSelectedMenu(menu);
    }
  };

  const handleMenuCloseClick = () => {
    setSelectedMenu(null);
  };

  const handleAssetSelectionOnCanvas = (
    id: string,
    selectedAsset: AssetType
  ): void => {
    if (id === '') {
      setAssetMenuVisible(false);
      return;
    }
    setSelectedAssetOnCanvas({
      id: id,
      selectedAsset: selectedAsset,
    });
    setAssetMenuVisible(true);
  };

  const handleAnimationSelect = (animationData: any): void => {
    if (!selectedSceneId) {
      toast.error(toastMessage('scene.selectFirst'));
      return;
    }

    const newAnimation: AnimationCharacter = {
      id: crypto.randomUUID(),
      animation: animationData,
      name: animationData.nm,
      pathPoints: [],
      position: { x: canvasWidth / 2, y: canvasHeight / 2 },
      scale: 1,
    };

    setSelectedAnimation((prevAnimations: AnimationCharacter[]) => {
      const updatedAnimation = [...prevAnimations, newAnimation];
      const newAnimationData = updatedAnimation.map(
        ({ animation, ...rest }) => rest
      );
      channel.publish('handleAnimationChange', newAnimationData);
      if (videoId) {
        videoService.patchVideo(videoId, { animations: newAnimationData });
      }
      return updatedAnimation;
    });

    // Find the scene's time range from the background row
    const backgroundRow = timelineData.find(
      (row) => row.id === 'background-row'
    );
    const sceneAction = backgroundRow?.actions.find(
      (action) => action.id === `background-action-${selectedSceneId}`
    );

    if (!sceneAction) {
      toast.error(toastMessage('scene.notFound'));
      return;
    }

    // Find an existing row that can accommodate this animation
    const existingRow = timelineData.find((row) => {
      if (row.id === 'background-row') return false;

      // Check if any action in this row overlaps with the new animation's time range
      const hasOverlappingAction = row.actions.some((action) => {
        // Check if the action's time range overlaps with the new animation's time range
        return (
          (action.start >= sceneAction.start &&
            action.start < sceneAction.end) ||
          (action.end > sceneAction.start && action.end <= sceneAction.end) ||
          (action.start <= sceneAction.start && action.end >= sceneAction.end)
        );
      });

      // If no overlapping actions found, this row can accommodate the new animation
      return !hasOverlappingAction;
    });

    // Create the new animation action
    const newAnimationAction = {
      id: 'animation-action-' + newAnimation.id,
      start: sceneAction.start,
      end: sceneAction.end,
      effectId: 'effect1',
      data: {
        src: newAnimation.id,
        name: newAnimation.name,
        ref: charactersRef,
        sceneId: selectedSceneId,
      } as ActionData,
    };

    // Update timeline data
    setTimelineData((prevData) => {
      // Ensure background row is first
      const backgroundRow = prevData.find((row) => row.id === 'background-row');
      if (!backgroundRow) return prevData;

      // Create new array with background row first
      const newData = [backgroundRow];

      if (existingRow) {
        // Add the new action to the existing row
        const updatedRow = {
          ...existingRow,
          actions: [...existingRow.actions, newAnimationAction],
        };

        // Add all other rows except background and the existing row
        const otherRows = prevData.filter(
          (row) => row.id !== 'background-row' && row.id !== existingRow.id
        );

        // Add the updated row and other rows
        newData.push(...otherRows, updatedRow);
      } else {
        // Create new row for the animation
        const newAnimationRow: CusTomTimelineRow = {
          id: newAnimation.id,
          name: newAnimation.name,
          start: sceneAction.start,
          end: sceneAction.end,
          actions: [newAnimationAction],
        };

        // Add all other rows except background
        const otherRows = prevData.filter((row) => row.id !== 'background-row');

        // Add the new animation row and other rows
        newData.push(...otherRows, newAnimationRow);
      }

      const actionData = extractTimelineActions(newData);
      channel.publish('handleTimelineEdit', actionData);

      if (videoId) {
        videoService.patchVideo(videoId, { timeline: actionData });
      }
      return newData;
    });

    // Update layers
    setLayers((prevLayers) => {
      const newLayers = [
        ...prevLayers,
        {
          id: newAnimation.id,
          name: newAnimation.name,
          type: AssetType.CHARACTER,
          visible: true,
          locked: false,
        },
      ];
      return newLayers;
    });

    // Publish the animation change with all animations
    channel.publish('handleAnimationChange', [
      ...selectedAnimation,
      newAnimation,
    ]);
  };

  const handleUploadedAssetSelect = (
    assetUrl: string,
    assetType?: string,
    fileName?: string
  ): void => {
    if (assetType === 'audio') {
      handleAudioSelect(assetUrl, fileName);
      return;
    }

    if (assetType === 'video') {
      const playableVideoUrl = getPlayableAssetUrl(assetUrl);
      getMediaDuration(playableVideoUrl, 'video')
        .then((duration) =>
          handleAddCharacterVideoToTimeline(
            assetUrl,
            fileName || decodeName(assetUrl),
            { duration, provider: 'upload' }
          )
        )
        .catch((error) => {
          console.error('Failed to add uploaded video to timeline:', error);
          toast.error(toastMessage('home.uploadedVideoAddFailed'));
        });
      return;
    }

    // Uploaded assets are kept as relative blob paths (stored against the
    // video id via getUploadURI); resolve them with the video's SAS token so
    // they can be rendered on the canvas. Direct/data URLs pass through.
    const playableUrl = getPlayableAssetUrl(assetUrl);

    const newUploadedAssset: AssetCharacter = {
      id: crypto.randomUUID(),
      url: playableUrl,
      position: { x: window.innerWidth / 2, y: window.innerHeight / 2 },
      scale: 0.5,
    };
    setSelectedUploadedAssets((prevAssets: AssetCharacter[]) => [
      ...prevAssets,
      newUploadedAssset,
    ]);

    setLayers((prevLayers: Layer[]) => {
      const newLayers = [
        ...prevLayers,
        {
          id: newUploadedAssset.id,
          name: 'Uploaded Asset Layer',
          type: AssetType.UPLOADEDASSET,
          visible: true,
          locked: false,
        },
      ];
      return newLayers;
    });
  };

  const handleAddComment = (text: string, parentId: string | null = null) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');

    const authorName = `${user.firstName} ${user.lastName}`;
    const newComment = {
      id: crypto.randomUUID(),
      author: {
        name: authorName,
        initials: `${user.firstName[0]}${user.lastName[0]}`,
        color: getAvatarColor(authorName),
      },
      text,
      pathPoints: parentId ? [] : commentDrawingPoints,
      // Store the drawing against the timestamp that was active while it was
      // drawn; text-only comments fall back to the current timeline position.
      timestamp: parentId
        ? 0
        : commentDrawingTimestamp > 0
        ? commentDrawingTimestamp
        : timelineState.current?.getTime() || 0,
      createdAt: new Date().toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      }),
      replies: [],
    };

    setComments((prevComments) => {
      let updatedComments;

      if (parentId) {
        updatedComments = prevComments.map((comment) =>
          comment.id === parentId
            ? { ...comment, replies: [...(comment.replies || []), newComment] }
            : comment
        );
      } else {
        updatedComments = [...prevComments, newComment];
      }

      channel.publish('handleCommentUpdate', updatedComments);
      if (videoId) {
        videoService.patchVideo(videoId, { comments: updatedComments });
      }

      return updatedComments;
    });

    setCommentDrawingPoints([]);
    commentDrawingPointsRef.current = [];
    setCommentDrawingTimestamp(0);

    // The comment drawing has been committed to this comment (which carries a
    // timestamp). Clear the live pencil strokes so they no longer persist on
    // the canvas at every timeline position; the saved drawing is rendered
    // separately and only appears at the comment's timestamp.
    if (!parentId && drawingRef.current) {
      drawingRef.current.destroy();
      drawingRef.current = null;
    }
  };

  const handleAddCommentDrawingPoint = (point: Position) => {
    // Capture the timeline timestamp for a new drawing stroke so the saved
    // drawing comment is stored against the position that was active while it
    // was drawn, just like comments typed in the comments section.
    if (commentDrawingPointsRef.current.length === 0) {
      setCommentDrawingTimestamp(timelineState.current?.getTime() || 0);
    }

    const nextPoints = [...commentDrawingPointsRef.current, point];
    commentDrawingPointsRef.current = nextPoints;
    setCommentDrawingPoints(nextPoints);
  };

  const handleDeleteAsset = (deletedAsset: {
    id: string;
    assetType: AssetType;
  }) => {
    switch (deletedAsset.assetType) {
      case AssetType.TEXT:
        removeTimelineActionsForAsset(deletedAsset.id, 'effect3');
        const textIDsToDelete: string[] = [];
        for (const [id, ref] of textRef.current.entries()) {
          if (ref.id === deletedAsset.id) {
            ref.destroy?.();
            textIDsToDelete.push(id); // This is the ID, not an index
          }
        }
        textIDsToDelete.forEach((id) => textRef.current.delete(id));

        setSelectedTexts((prevTexts) => {
          const newTexts = prevTexts.filter(
            (text) => text.id !== deletedAsset.id
          );

          debouncedPatchText.cancel();
          debouncedTextDataRef.current = newTexts;

          const resp = newTexts.length > 0 ? newTexts : [];
          channel.publish('handleTextUpdate', resp);
          if (videoId) {
            videoService.patchVideo(videoId, { texts: resp });
          }

          return resp;
        });
        break;
      case AssetType.CHARACTER:
      case AssetType.PROP:
        setSelectedAnimation((prevAnimations) => {
          const newAnimations = prevAnimations.filter(
            (animation) => animation.id !== deletedAsset.id
          );
          console.log('New animations: ', newAnimations);
          if (videoId) {
            const resp = newAnimations.map(({ animation, ...rest }) => rest);
            channel.publish('handleAnimationChange', resp);
            videoService.patchVideo(videoId, { animations: resp });
          }
          return newAnimations;
        });
        const characterIDsToDelete: string[] = [];
        for (const [id, ref] of charactersRef.current.entries()) {
          if (ref.id === deletedAsset.id) {
            ref.destroy?.();
            characterIDsToDelete.push(id); // This is the ID, not an index
          }
        }

        characterIDsToDelete.forEach((id) => charactersRef.current.delete(id));
        break;
      case AssetType.RIGGED_CHARACTER:
        setRiggedCharacters((prev) =>
          prev.filter((char) => char.id !== deletedAsset.id)
        );
        setTimelineData((prevData) => {
          const newData = prevData.filter((row) => row.id !== deletedAsset.id);
          const actionData = extractTimelineActions(newData);
          channel.publish('handleTimelineEdit', actionData);
          if (videoId) {
            videoService.patchVideo(videoId, { timeline: actionData });
          }
          return newData;
        });

        break;
      case AssetType.EFFECT:
        setSelectedEffect((prevEffects) => {
          const newEffects = prevEffects.filter(
            (effect) => effect.id !== deletedAsset.id
          );
          if (videoId) {
            channel.publish('handleEffectChange', newEffects);
            videoService.patchVideo(videoId, { effects: newEffects });
          }
          return newEffects;
        });
        break;
      case AssetType.BACKGROUND:
        handleAddBackground(null);
        if (videoId) {
          channel.publish('handleBackgroundChange', null);
          videoService.patchVideo(videoId, { backgroundImage: null });
        }
        break;
    }
    setLayers((prevLayers) => {
      const newLayers = prevLayers.filter(
        (layer) => layer.id !== deletedAsset.id
      );
      return newLayers;
    });
    setSelectedAssetOnCanvas({ id: '', selectedAsset: AssetType.NONE });
  };

  const debouncedTextDataRef = useRef<TextProps[]>([]);

  // Stable debounced PATCH for text edits. Must be memoized: an inline
  // debounce(...) would mint a fresh timer on every render, so a burst of
  // edits (e.g. dragging the color picker) would fire one PATCH per render
  // instead of a single trailing call.
  const debouncedPatchText = useMemo(
    () =>
      debounce((id: string | undefined) => {
        if (id) {
          videoService.patchVideo(id, {
            texts: debouncedTextDataRef.current,
          });
        }
      }, 500),
    []
  );

  const updateTextProperties = async (
    id: string,
    properties: Partial<TextProps>
  ) => {
    setSelectedTexts((prevTexts) => {
      const newTexts = prevTexts.map((text) =>
        text.id === id ? { ...text, ...properties } : text
      );

      debouncedTextDataRef.current = newTexts;

      channel.publish('handleTextUpdate', debouncedTextDataRef.current);
      return newTexts;
    });

    debouncedPatchText(videoId);
  };

  const debouncedAnimationDataRef = useRef<AnimationCharacter[]>([]);

  // Stable debounced PATCH for character/animation edits (same memoization
  // requirement as debouncedPatchText above).
  const debouncedPatchVideo = useMemo(
    () =>
      debounce((id: string | undefined) => {
        if (id) {
          videoService.patchVideo(id, {
            animations: debouncedAnimationDataRef.current,
          });
        }
      }, 500),
    []
  );

  const debouncedSceneOrderRef = useRef<{
    scenes: Scene[];
    // Null when only scene metadata (e.g. durations) changed and the
    // timeline itself was already persisted live by the engine's onChange.
    actionData?: any | null;
  } | null>(null);

  // Trailing-edge persist for scene reorders. SceneMenu fires onSceneDrag
  // from useDrop hover, so a single drag produces a burst of reorder calls —
  // persisting each one would spam PATCH. This collapses the burst into one
  // publish + PATCH after the drop settles (same pattern as the text and
  // animation debounces above).
  const debouncedPersistSceneOrder = useMemo(
    () =>
      debounce((id: string | undefined) => {
        const pending = debouncedSceneOrderRef.current;
        if (!id || !pending) return;
        channel.publish('handleScenesUpdate', pending.scenes);
        const payload: { scenes: Scene[]; timeline?: any } = {
          scenes: pending.scenes,
        };
        if (pending.actionData) {
          channel.publish('handleTimelineEdit', pending.actionData);
          payload.timeline = pending.actionData;
        }
        videoService.patchVideo(id, payload);
      }, 800),
    [channel]
  );

  // Cancel pending trailing calls on unmount so no PATCH fires after Home
  // is gone and no timer is left dangling.
  useEffect(() => {
    return () => {
      debouncedPatchText.cancel();
      debouncedPatchVideo.cancel();
      debouncedPersistSceneOrder.cancel();
    };
  }, [debouncedPatchText, debouncedPatchVideo, debouncedPersistSceneOrder]);

  const updateCharacterProperties = (
    id: string,
    properties: Partial<AnimationCharacter>
  ) => {
    setSelectedAnimation((prevAnimations) => {
      const newAnimations = prevAnimations.map((character) =>
        character.id === id ? { ...character, ...properties } : character
      );

      //ably doesn't support large object sharing, so we are sending only the required data
      debouncedAnimationDataRef.current = newAnimations.map(
        ({ animation, ...rest }) => rest
      );
      channel.publish(
        'handleAnimationChange',
        debouncedAnimationDataRef.current
      );
      return newAnimations;
    });

    debouncedPatchVideo(videoId);
  };

  const handleAudioRecordingComplete = (
    recordedAudioFile: File,
    duration: string
  ) => {
    const audioId = crypto.randomUUID();
    console.log('Recorded audio file:', recordedAudioFile);
    if (!videoId) {
      toast.error(toastMessage('common.videoIdMissing'));
      return;
    }
    handleUploadAudio(recordedAudioFile, videoId).then((resultLocation) => {
      if (!resultLocation) return;

      const newAudio: AudioElement = {
        id: audioId,
        url: resultLocation,
      };

      const audioUrl = resultLocation;
      const backgroundRow = timelineData.find(
        (row) => row.id === 'background-row'
      );
      const sceneAction = backgroundRow?.actions.find(
        (action) => action.id === `background-action-${selectedSceneId}`
      );

      if (!sceneAction) {
        toast.error(toastMessage('scene.notFound'));
        return;
      }

      // Place the audio at the current timeline cursor/playhead position so it
      // starts exactly where the cursor is instead of always at the scene start.
      const insertTime = timelineState.current?.getTime() || sceneAction.start;
      const audioDuration = parseTimeToSeconds(duration);

      const newAudioRow: CusTomTimelineRow = {
        id: newAudio.id,
        name: decodeName(recordedAudioFile.name),
        start: insertTime,
        end: insertTime + audioDuration,
        actions: [
          {
            id: 'audio-action-' + newAudio.id,
            start: insertTime,
            end: insertTime + audioDuration,
            effectId: 'effect0',
            data: {
              src: audioUrl,
              name: decodeName(recordedAudioFile.name),
              ref: null,
              sceneId: selectedSceneId,
            } as ActionData,
          },
        ],
      };

      // Always add to timeline
      setTimelineData((prevData) => {
        // Ensure background row is first
        const backgroundRow = prevData.find(
          (row) => row.id === 'background-row'
        );
        if (!backgroundRow) return prevData;

        // Create new array with background row first
        const newData = [backgroundRow];

        // Add all other rows except background
        const otherRows = prevData.filter((row) => row.id !== 'background-row');

        // Add all other rows
        newData.push(...otherRows);

        // Add the new audio row
        newData.push(newAudioRow);

        const actionData = extractTimelineActions(newData);
        channel.publish('handleTimelineEdit', actionData);

        if (videoId) {
          videoService.patchVideo(videoId, { timeline: actionData });
        }
        return newData;
      });
      setSelectedAudio((prevAudio) => [...prevAudio, newAudio]);
    });
  };

  const handleAiAudioRequest = async (
    data: AudioGenerationRequest
  ): Promise<GeneratedAudioResult> => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const analytics = await getGaAnalyticsPayload();
    const result = await handleAudioGeneration(
      {
        prompt: data.prompt,
        style: data.style,
        title: data.title,
        ...(analytics ? { analytics } : {}),
      },
      token,
      (event) => {
        console.log('Event received:', event);
      },
      (finalResult) => {
        console.log('Stream completed, audio:', finalResult);
      }
    );

    return result;
  };

  const handleAiBackgroundRequest = async (
    data: BackgroundGenerationRequest
  ) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      try {
        const response = await handleBackgroundGeneration(data.prompt, token);
        if (response.data && response.data.length > 0) {
          toast.success(toastMessage('home.backgroundGenerated'));
          return response.data[0].imageUrl;
        }
      } catch (error: any) {
        // Credit-gate errors propagate to the NavigationMenu upsell banners.
        if (
          error instanceof NoActiveEntitlementError ||
          error instanceof InsufficientPixlesError
        ) {
          throw error;
        }
        const errorMessage =
          error.message || toastMessage('home.backgroundGenerateFailed');
        toast.error(errorMessage);
      }
    }
  };

  const handleSaveAndCanvasImportBackground = (ImageUrl: string) => {
    // add save logic
    handleBackgroundSelect(ImageUrl);
  };

  const handleSaveAndCanvasImportAudio = async (
    audioUrl: string,
    title?: string,
    duration?: number
  ): Promise<void> => {
    if (!audioUrl) {
      toast.error(toastMessage('home.noGeneratedAudio'));
      return;
    }
    if (!videoId) {
      toast.error(toastMessage('common.videoIdMissing'));
      return;
    }

    // Download the generated audio (from the stable source_audio_url) and persist
    // it into the video's blob storage. The timeline then stores the returned
    // relative blob path and re-signs it with the video's SAS token at playback
    // (mirrors the recorded-audio add-to-timeline flow).
    try {
      const response = await fetch(audioUrl);
      if (!response.ok) {
        throw new Error(`Failed to download generated audio: ${response.statusText}`);
      }
      const blob = await response.blob();

      const safeTitle =
        (title || 'generated-audio').trim() || 'generated-audio';
      const ext =
        audioUrl.split('?')[0].match(/\.(mp3|wav|ogg|m4a|aac|flac|mp4|webm)$/i)?.[1] ||
        'mp3';
      const storageName = `${safeTitle.replace(/[^a-z0-9-_]+/gi, '_')}.${ext}`;

      const audioFile = new File([blob], storageName, {
        type: blob.type || 'audio/mpeg',
      });

      const resultLocation = await handleUploadAudio(audioFile, videoId);
      if (!resultLocation) {
        toast.error(toastMessage('home.audioSaveStorageFailed'));
        return;
      }

      // If the generation API did not return a duration, probe the playable
      // source URL (public CDN) before persisting so the timeline clip can be
      // sized reliably; otherwise addAudioToTimeline falls back internally.
      let timelineDuration: number | undefined = duration;
      if (typeof timelineDuration !== 'number' || timelineDuration <= 0) {
        try {
          timelineDuration = await getMediaDuration(audioUrl, 'audio');
        } catch {
          timelineDuration = undefined;
        }
      }

      await addAudioToTimeline(resultLocation, safeTitle, timelineDuration);
      toast.success(toastMessage('home.audioAdded'));
    } catch (error: any) {
      console.error('Failed to save generated audio:', error);
      toast.error(
        error?.message || toastMessage('home.audioSaveFailed')
      );
    }
  };

  const updateAssetProperties = (
    index: number,
    properties: Partial<AssetCharacter>
  ) => {
    setSelectedUploadedAssets((prevAsseets) => {
      const newAssets = [...prevAsseets];
      newAssets[index] = {
        ...newAssets[index],
        ...properties,
      };
      return newAssets;
    });
  };

  const targetAspectRatio = 16 / 9;
  // Compact stages: on landscape phones height is the binding axis for a
  // 16:9 canvas, so shrink the chrome reservations to match the slimmed CSS
  // (60px scenes rail, ~16px wrapper padding). On portrait phones the stage
  // column is only ~230px wide and width is binding, so trim the desktop
  // 72px reservation down to the real ~40px wrapper padding. Reactive via
  // the ResizeObserver-fed viewport, so rotation updates it with no extra
  // listeners. Desktop keeps 72/64.
  const isLandscapePhoneStage =
    canvasViewport.height < 500 &&
    canvasViewport.width >= canvasViewport.height;
  const isPortraitPhoneStage =
    canvasViewport.width < 500 &&
    canvasViewport.height > canvasViewport.width;
  const canvasAvailableWidth = Math.max(
    1,
    canvasViewport.width -
      (isLandscapePhoneStage ? 64 : isPortraitPhoneStage ? 44 : 72)
  );
  const canvasAvailableHeight = Math.max(
    1,
    canvasViewport.height -
      (isLandscapePhoneStage ? 20 : isPortraitPhoneStage ? 40 : 64)
  );
  const canvasScale = 1;
  const canvasWidth = Math.floor(
    Math.min(
      canvasAvailableWidth * canvasScale,
      canvasAvailableHeight * canvasScale * targetAspectRatio
    )
  );
  const canvasHeight = Math.floor(canvasWidth / targetAspectRatio);
  const floatingMenuTop = Math.max(
    6,
    Math.floor((canvasViewport.height - canvasHeight) / 2) - 4
  );

  const handleAddCharacterVideoToTimeline = async (
    videoUrl: string,
    prompt: string,
    options: {
      duration?: number;
      provider?: string;
      trim?: VideoTrimRange;
      mute?: boolean;
    } = {}
  ) => {
    // Save the generated video to backend storage and keep its relative blob path
    // for the timeline document (mirrors the audio add-to-timeline flow). The
    // frontend resolves the path into a playable URL using the SAS token that
    // the backend returns on the updated video document.
    let generatedDuration = options.duration;
    const provider = options.provider ?? 'seedance';
    const token = localStorage.getItem('authToken');

    if (provider !== 'upload' && (options.trim || options.mute)) {
      if (!token || !videoId) {
        toast.error(toastMessage('home.videoSaveFailed'));
        return;
      }
      try {
        const saved = await trimGeneratedVideo(
          videoUrl,
          videoId,
          token,
          options.trim,
          options.mute === true
        );
        videoUrl = saved.location;
        generatedDuration = saved.durationSeconds;
      } catch (error: any) {
        toast.error(
          error?.message || toastMessage('home.videoSaveFailed')
        );
        return;
      }
    } else if (provider !== 'upload' && token && videoId) {
      try {
        videoUrl = await handleUploadGeneratedVideo(videoUrl, videoId, token);
      } catch (error: any) {
        toast.error(
          error?.message || toastMessage('home.videoSaveFailed')
        );
        return;
      }
    }

    const sceneDuration =
      generatedDuration && generatedDuration > 0 ? generatedDuration : 10;

    const backgroundRow = timelineData.find(
      (row) => row.id === 'background-row'
    );
    const lastSceneAction =
      backgroundRow?.actions[backgroundRow.actions.length - 1];
    const shouldReuseEmptyScene = lastSceneAction
      ? !doesSceneHaveContent(lastSceneAction, timelineData)
      : false;
    const sceneIdToUse = shouldReuseEmptyScene
      ? lastSceneAction!.id.replace('background-action-', '')
      : crypto.randomUUID();
    const sceneNameToUse =
      scenes.find((scene) => scene.id === sceneIdToUse)?.name ||
      getNextSceneName(scenes);
    const sceneStart = shouldReuseEmptyScene && lastSceneAction
      ? lastSceneAction.start
      : lastSceneAction
        ? lastSceneAction.end
        : 0;
    const sceneEnd = sceneStart + sceneDuration;
    const backgroundActionId = `background-action-${sceneIdToUse}`;

    const updatedScenes = shouldReuseEmptyScene
      ? scenes.map((scene) =>
          scene.id === sceneIdToUse
            ? {
                ...scene,
                name: sceneNameToUse,
                duration: sceneDuration,
                backgroundImage: videoUrl,
              }
            : scene
        )
      : [
          ...scenes,
          {
            id: sceneIdToUse,
            name: sceneNameToUse,
            duration: sceneDuration,
            backgroundImage: videoUrl,
          },
        ];

    const updatedTimelineData = timelineData.map((row) => {
      if (row.id !== 'background-row') return row;

      const nextActions = shouldReuseEmptyScene
        ? row.actions.map((action) =>
            action.id === backgroundActionId
              ? {
                  ...action,
                  start: sceneStart,
                  end: sceneEnd,
                  data: {
                    ...action.data,
                    src: sceneIdToUse,
                    name: sceneNameToUse,
                    ref: setBackgroundImage,
                    imageData: videoUrl,
                    videoData: videoUrl,
                    provider,
                    prompt,
                  },
                }
              : action
          )
        : [
            ...row.actions,
            {
              id: backgroundActionId,
              start: sceneStart,
              end: sceneEnd,
              effectId: 'effect4',
              data: {
                src: sceneIdToUse,
                name: sceneNameToUse,
                ref: setBackgroundImage,
                imageData: videoUrl,
                videoData: videoUrl,
                provider,
                prompt,
              },
            },
          ];

      return {
        ...row,
        actions: nextActions,
      };
    });

    const actionData = extractTimelineActions(updatedTimelineData);

    channel.publish('handleScenesUpdate', updatedScenes);
    channel.publish('handleTimelineEdit', actionData);

    let savedVideo: Video | null = null;
    if (videoId) {
      savedVideo = await videoService.patchVideo(videoId, {
        scenes: updatedScenes,
        timeline: actionData,
        backgroundImage: videoUrl,
      });

      // Ensure we have an up-to-date video document carrying the SAS token so
      // the stored relative path can be rendered. The PATCH response should
      // include it, but fall back to a fresh GET if not.
      if (!savedVideo?.sas?.token) {
        try {
          savedVideo = (await videoService.getVideoById(videoId)) ?? savedVideo;
        } catch (error) {
          console.error('Failed to refresh video after add-to-timeline:', error);
        }
      }

      currentVideoRef.current = savedVideo;
      cacheVideoSas(videoId, savedVideo?.sas);
    }

    setScenes(savedVideo?.scenes ?? updatedScenes);
    setTimelineData(updatedTimelineData);

    setSelectedSceneId(sceneIdToUse);
    // Store the relative path in the document; the setBackgroundImage wrapper
    // resolves it into a playable URL using the current video's SAS token.
    setBackgroundImage(
      savedVideo?.backgroundImage || videoUrl,
      savedVideo?.sas?.token
    );
    // Move the engine cursor to the new scene (mirrors handleSelectScene).
    // Without this the cursor stays at 0 while the canvas previews the new
    // video, and the first play renders the new video: BackgroundControl
    // sees the scene-1 signature as already active and skips the swap, so
    // the stale preview keeps playing. Aligning the cursor makes canvas,
    // cursor, and playback agree; scrubbing back to 0 still replays
    // correctly from the first scene.
    setTimeout(() => {
      if (timelineState.current) {
        timelineState.current.setTime(sceneStart);
        timelineState.current.reRender();
      }
    }, 50);
    setIsCharacterVideoUnsaved(false);
    setIsCharacterVideoGeneratorOpen(false);
  };

  /**
   * Opens the Character Studio from the video generator so the user can modify
   * the character they intend to use. The video generator stays open underneath
   * so its prompt/script/preview state is preserved when they come back.
   */
  const handleOpenCharacterStudioFromVideoGenerator = (
    characterId?: string
  ) => {
    setStudioFocusCharacterId(characterId);
    setIsCharacterLibraryOpen(true);
  };

  const handleCloseCharacterLibrary = () => {
    setIsCharacterLibraryOpen(false);
    setStudioFocusCharacterId(undefined);
    setCharacterLibraryRevision((revision) => revision + 1);
  };

  const handleCloseEnvironmentLibrary = () => {
    setIsEnvironmentLibraryOpen(false);
    setEnvironmentLibraryRevision((revision) => revision + 1);
  };

  const handleAddScene = () => {
    const newSceneId = crypto.randomUUID();
    const newScene = {
      id: newSceneId,
      name: getNextSceneName(scenes),
      duration: 10,
    };

    // Add the new scene to scenes state
    setScenes((prevScenes) => {
      const updatedScenes = [...prevScenes, newScene];

      channel.publish('handleScenesUpdate', updatedScenes);
      if (videoId) {
        videoService.patchVideo(videoId, { scenes: updatedScenes });
      }
      return updatedScenes;
    });

    // Find the last scene's end time in the timeline
    const backgroundRow = timelineData.find(
      (row) => row.id === 'background-row'
    );
    const lastSceneAction =
      backgroundRow?.actions[backgroundRow.actions.length - 1];
    const newSceneStart = lastSceneAction ? lastSceneAction.end : 0;
    const newSceneEnd = newSceneStart + 10; // Default 10 second duration

    // Add the new scene to the timeline
    setTimelineData((prevData) => {
      const newData = prevData.map((row) => {
        if (row.id === 'background-row') {
          return {
            ...row,
            actions: [
              ...row.actions,
              {
                id: `background-action-${newSceneId}`,
                start: newSceneStart,
                end: newSceneEnd,
                effectId: 'effect4',
                data: {
                  src: newSceneId,
                  name: newScene.name,
                  ref: setBackgroundImage,
                  imageData: null,
                },
              },
            ],
          };
        }
        return row;
      });
      const actionData = extractTimelineActions(newData);
      channel.publish('handleTimelineEdit', actionData);

      if (videoId) {
        videoService.patchVideo(videoId, { timeline: actionData });
      }
      return newData;
    });
  };

  const handleSelectScene = (sceneId: string) => {
    setSelectedSceneId(sceneId);
    const scene = scenes.find((s) => s.id === sceneId);

    // Find the background action for this scene
    const backgroundRow = timelineData.find(
      (row) => row.id === 'background-row'
    );
    const sceneAction = backgroundRow?.actions.find(
      (action) => action.id === `background-action-${sceneId}`
    );
    if (sceneAction) {
      // Use setTimeout to ensure this runs after the current render cycle
      setTimeout(() => {
        if (timelineState.current) {
          console.log('Setting time to:', sceneAction.start);
          timelineState.current.setTime(sceneAction.start);
          timelineState.current.reRender();
        }
      }, 50);
    }

    if (scene?.backgroundImage) {
      setBackgroundImage(scene.backgroundImage);
    }
  };

  const handleUpdateSceneName = (sceneId: string, newName: string) => {
    // Update the scene name in the scenes state
    setScenes((prevScenes) => {
      const updatedScenes = prevScenes.map((scene) =>
        scene.id === sceneId ? { ...scene, name: newName } : scene
      );

      channel.publish('handleScenesUpdate', updatedScenes);

      if (videoId) {
        videoService.patchVideo(videoId, { scenes: updatedScenes });
      }

      return updatedScenes;
    });

    // Update the scene name in the timeline data
    setTimelineData((prevData) => {
      const newData = prevData.map((row) => {
        if (row.id === 'background-row') {
          return {
            ...row,
            actions: row.actions.map((action) => {
              if (action.id === `background-action-${sceneId}`) {
                return {
                  ...action,
                  data: {
                    ...action.data,
                    name: newName,
                  },
                };
              }
              return action;
            }),
          };
        }
        return row;
      });

      const actionData = extractTimelineActions(newData);
      channel.publish('handleTimelineEdit', actionData);
      if (videoId) {
        videoService.patchVideo(videoId, { timeline: actionData });
      }

      return newData;
    });
  };

  const handleDeleteScene = (sceneId: string) => {
    if (scenes.length <= 1) {
      toast.info(toastMessage('scene.required'));
      return;
    }

    // Remove the scene
    const updatedScenes = scenes.filter((scene) => scene.id !== sceneId);
    setScenes(updatedScenes);
    channel.publish('handleScenesUpdate', updatedScenes);
    if (videoId) {
      videoService.patchVideo(videoId, { scenes: updatedScenes });
    }

    // Remove the corresponding background
    setSelectedBackgrounds((prev) => prev.filter((bg) => bg.id !== sceneId));

    // Find the scene's time range
    const backgroundRow = timelineData.find(
      (row) => row.id === 'background-row'
    );
    const sceneAction = backgroundRow?.actions.find(
      (action) => action.id === `background-action-${sceneId}`
    );

    if (sceneAction) {
      // Remove all entities within the scene's time range
      setTimelineData((prevData) => {
        const newData = prevData.filter((row) => {
          // Keep the background row
          if (row.id === 'background-row') return true;

          // Check if any action in this row overlaps with the scene
          const hasOverlappingAction = row.actions.some(
            (action) =>
              (action.start >= sceneAction.start &&
                action.start < sceneAction.end) ||
              (action.end > sceneAction.start &&
                action.end <= sceneAction.end) ||
              (action.start <= sceneAction.start &&
                action.end >= sceneAction.end)
          );

          return !hasOverlappingAction;
        });

        // Update the timeline to remove gaps
        const updatedData = newData.map((row) => {
          if (row.id === 'background-row') {
            // Shift subsequent scenes to fill the gap
            const updatedActions = row.actions
              .filter((action) => action.id !== `background-action-${sceneId}`)
              .map((action) => {
                if (action.start > sceneAction.start) {
                  const timeShift = sceneAction.end - sceneAction.start;
                  return {
                    ...action,
                    start: action.start - timeShift,
                    end: action.end - timeShift,
                  };
                }
                return action;
              });

            return {
              ...row,
              actions: updatedActions,
            };
          }
          return row;
        });

        const actionData = extractTimelineActions(updatedData);
        channel.publish('handleTimelineEdit', actionData);

        if (videoId) {
          videoService.patchVideo(videoId, { timeline: actionData });
        }
        // Publish the updated timeline data
        return updatedData;
      });
    }
  };

  const getTimelineActionAssetId = (
    action: CustomTimelineAction,
    row: CusTomTimelineRow
  ) => {
    if (action.effectId === 'effect0') return row.id;
    return action.data?.src ?? row.id;
  };

  const getTimelineActionAssetKey = (
    action: CustomTimelineAction,
    row: CusTomTimelineRow
  ) => {
    if (action.effectId === 'effect0') return action.data?.src ?? row.id;
    return action.data?.src ?? row.id;
  };

  const hasOtherTimelineActionForAsset = (
    targetAction: CustomTimelineAction,
    targetRow: CusTomTimelineRow
  ) => {
    const targetKey = getTimelineActionAssetKey(targetAction, targetRow);

    return timelineData.some((row) =>
      row.actions.some(
        (action) =>
          action.id !== targetAction.id &&
          action.effectId === targetAction.effectId &&
          getTimelineActionAssetKey(action, row) === targetKey
      )
    );
  };

  const removeTimelineAction = (actionId: string) => {
    setTimelineData((prevData) => {
      let didRemoveAction = false;

      const newData = prevData.reduce<CusTomTimelineRow[]>((rows, row) => {
        if (row.id === 'background-row') {
          rows.push(row);
          return rows;
        }

        const actions = row.actions.filter((action) => action.id !== actionId);

        if (actions.length === row.actions.length) {
          rows.push(row);
          return rows;
        }

        didRemoveAction = true;

        if (actions.length > 0) {
          rows.push({
            ...row,
            actions,
            start: Math.min(...actions.map((action) => action.start)),
            end: Math.max(...actions.map((action) => action.end)),
          });
        }

        return rows;
      }, []);

      if (!didRemoveAction) return prevData;

      const actionData = extractTimelineActions(newData);
      channel.publish('handleTimelineEdit', actionData);

      if (videoId) {
        videoService.patchVideo(videoId, { timeline: actionData });
      }

      return newData;
    });
  };

  const removeTimelineActionsForAsset = (
    assetId: string,
    effectId: CustomTimelineAction['effectId']
  ) => {
    setTimelineData((prevData) => {
      let didRemoveAction = false;

      const newData = prevData.reduce<CusTomTimelineRow[]>((rows, row) => {
        if (row.id === 'background-row') {
          rows.push(row);
          return rows;
        }

        const actions = row.actions.filter((action) => {
          const actionAssetId = action.data?.src ?? row.id;
          const shouldRemove =
            action.effectId === effectId && actionAssetId === assetId;

          if (shouldRemove) {
            didRemoveAction = true;
            return false;
          }

          return true;
        });

        if (actions.length === 0) {
          return rows;
        }

        rows.push({
          ...row,
          actions,
          start: Math.min(...actions.map((action) => action.start)),
          end: Math.max(...actions.map((action) => action.end)),
        });

        return rows;
      }, []);

      if (!didRemoveAction) return prevData;

      const actionData = extractTimelineActions(newData);
      channel.publish('handleTimelineEdit', actionData);

      if (videoId) {
        videoService.patchVideo(videoId, { timeline: actionData });
      }

      return newData;
    });
  };

  const removeLayerForAsset = (assetId: string) => {
    setLayers((prevLayers) => {
      const newLayers = prevLayers.filter((layer) => layer.id !== assetId);

      if (newLayers.length === prevLayers.length) return prevLayers;

      channel.publish('handleLayersUpdate', newLayers);

      if (videoId) {
        videoService.patchVideo(videoId, {
          layers: getPersistableLayers(newLayers),
        });
      }

      return newLayers;
    });
  };

  const removeTextRefs = (textId: string) => {
    const textIDsToDelete: string[] = [];

    for (const [id, ref] of textRef.current.entries()) {
      if (ref.id === textId) {
        ref.destroy?.();
        textIDsToDelete.push(id);
      }
    }

    textIDsToDelete.forEach((id) => textRef.current.delete(id));
  };

  const removeCharacterRefs = (characterId: string) => {
    const characterIDsToDelete: string[] = [];

    for (const [id, ref] of charactersRef.current.entries()) {
      if (ref.id === characterId) {
        ref.destroy?.();
        characterIDsToDelete.push(id);
      }
    }

    characterIDsToDelete.forEach((id) => charactersRef.current.delete(id));
  };

  const removeRiggedCharacterRefs = (characterId: string) => {
    const characterIDsToDelete: string[] = [];

    for (const [id, ref] of riggedCharactersRef.current.entries()) {
      if (ref.id === characterId) {
        ref.destroy?.();
        characterIDsToDelete.push(id);
      }
    }

    characterIDsToDelete.forEach((id) =>
      riggedCharactersRef.current.delete(id)
    );
  };

  const handleDeleteTimelineAction = (
    action: CustomTimelineAction,
    row: CusTomTimelineRow
  ) => {
    const assetId = getTimelineActionAssetId(action, row);

    if (action.effectId === 'effect4') {
      return;
    }

    const shouldRemoveAssetState = !hasOtherTimelineActionForAsset(action, row);

    removeTimelineAction(action.id);

    if (!shouldRemoveAssetState) return;

    removeLayerForAsset(assetId);
    setSelectedAssetOnCanvas({ id: '', selectedAsset: AssetType.NONE });

    switch (action.effectId) {
      case 'effect0':
        setSelectedAudio((prevAudio) => {
          const updatedAudio = prevAudio.filter(
            (audio) => audio.id !== assetId
          );
          channel.publish('handleAudioUpdate', updatedAudio);

          if (videoId) {
            videoService.patchVideo(videoId, { audio: updatedAudio });
          }

          return updatedAudio;
        });
        break;
      case 'effect1':
        removeCharacterRefs(assetId);
        setSelectedAnimation((prevAnimations) => {
          const newAnimations = prevAnimations.filter(
            (animation) => animation.id !== assetId
          );
          const response = newAnimations.map(({ animation, ...rest }) => rest);
          channel.publish('handleAnimationChange', response);

          if (videoId) {
            videoService.patchVideo(videoId, { animations: response });
          }

          return newAnimations;
        });
        break;
      case 'effect2':
        setSelectedEffect((prevEffects) => {
          const newEffects = prevEffects.filter(
            (effect) => effect.id !== assetId
          );
          channel.publish('handleEffectChange', newEffects);

          if (videoId) {
            videoService.patchVideo(videoId, { effects: newEffects });
          }

          return newEffects;
        });
        break;
      case 'effect3':
        removeTextRefs(assetId);
        setSelectedTexts((prevTexts) => {
          debouncedPatchText.cancel();
          const newTexts = prevTexts.filter((text) => text.id !== assetId);
          debouncedTextDataRef.current = newTexts;
          channel.publish('handleTextUpdate', newTexts);

          if (videoId) {
            videoService.patchVideo(videoId, { texts: newTexts });
          }

          return newTexts;
        });
        break;
      case 'effect5':
        removeRiggedCharacterRefs(assetId);
        setRiggedCharacters((prevCharacters) =>
          prevCharacters.filter((character) => character.id !== assetId)
        );
        break;
    }
  };

  const handleDuplicateScene = (sceneId: string) => {
    const sceneToDuplicate = scenes.find((scene) => scene.id === sceneId);
    if (!sceneToDuplicate) return;

    const newBackground = {
      id: `bg-${Date.now()}`,
      name: 'Background',
      imageData: sceneToDuplicate.backgroundImage,
    };

    // Find the scene's time range
    const backgroundRow = timelineData.find(
      (row) => row.id === 'background-row'
    );
    if (!backgroundRow) return;

    const sceneAction = backgroundRow.actions.find(
      (action) => action.id === `background-action-${sceneId}`
    );
    if (!sceneAction) return;

    const duplicatedBackgroundImage =
      sceneAction.data?.videoData ||
      sceneAction.data?.imageData ||
      sceneToDuplicate.backgroundImage;
    const duplicatedDuration = sceneAction.end - sceneAction.start;
    const newScene = {
      id: newBackground.id,
      name: getDuplicateSceneName(sceneToDuplicate.name, scenes),
      backgroundId: newBackground.id,
      backgroundImage: duplicatedBackgroundImage,
      duration: duplicatedDuration,
    };
    const updatedScenes = [...scenes, newScene];
    setScenes(updatedScenes);
    channel.publish('handleScenesUpdate', updatedScenes);

    // Find the last scene's end time
    const lastSceneAction = backgroundRow.actions[backgroundRow.actions.length - 1];
    const newSceneStart = lastSceneAction ? lastSceneAction.end : 0;
    const newSceneEnd = newSceneStart + duplicatedDuration;

    // Add the new scene to the background row and duplicate all elements
    setTimelineData((prevData) => {
      // First, create a new array with the background row updated
      const newData = prevData.map((row) => {
        if (row.id === 'background-row') {
          return {
            ...row,
            actions: [
              ...row.actions,
              {
                id: `background-action-${newBackground.id}`,
                start: newSceneStart,
                end: newSceneEnd,
                effectId: 'effect4',
                data: {
                  ...sceneAction.data,
                  src: newBackground.id,
                  name: newScene.name,
                  ref: setBackgroundImage,
                  imageData: duplicatedBackgroundImage,
                },
              },
            ],
          };
        }

        // For non-background rows, check if they have actions in the original scene
        const actionsInScene = row.actions.filter(
          (action) =>
            (action.start >= sceneAction.start &&
              action.start < sceneAction.end) ||
            (action.end > sceneAction.start &&
              action.end <= sceneAction.end) ||
            (action.start <= sceneAction.start &&
              action.end >= sceneAction.end)
        );

        if (actionsInScene.length > 0) {
          // Create duplicates of actions that are in the original scene
          const duplicatedActions = actionsInScene.map((action) => {
            // Calculate the relative position within the scene
            const relativeStart = Math.max(0, action.start - sceneAction.start);
            const relativeEnd = Math.min(
              sceneAction.end - sceneAction.start,
              action.end - sceneAction.start
            );

            // Create a new action with adjusted timing for the new scene
            return {
              ...action,
              id: `${action.id}-${Date.now()}`,
              start: newSceneStart + relativeStart,
              end: newSceneStart + relativeEnd,
              data: {
                ...action.data,
                sceneId: newBackground.id, // Update the sceneId to point to the new scene
              },
            };
          });

          // Add the duplicated actions to the existing row
          return {
            ...row,
            actions: [...row.actions, ...duplicatedActions],
          };
        }

        return row;
      });

      const actionData = extractTimelineActions(newData);
      channel.publish('handleTimelineEdit', actionData);

      if (videoId) {
        videoService.patchVideo(videoId, {
          scenes: updatedScenes,
          timeline: actionData,
        });
      }

      return newData;
    });
  };

  const handleTransitionScene = (sceneId: string) => {
    console.log('Transitioning scene:', sceneId);
  };

  const reorderTimelineForSceneOrder = (
    prevData: CusTomTimelineRow[],
    orderedScenes: Scene[],
    draggedIndex: number,
    droppedIndex: number
  ): CusTomTimelineRow[] => {
    // Find the background row
    const backgroundRow = prevData.find((row) => row.id === 'background-row');
    if (!backgroundRow) return prevData;

    // Create new array of background actions in the new order
    const newBackgroundActions = [...backgroundRow.actions];
    const [draggedAction] = newBackgroundActions.splice(draggedIndex, 1);
    newBackgroundActions.splice(droppedIndex, 0, draggedAction);

    // Calculate new start and end times for all scenes
    let currentTime = 0;
    const updatedBackgroundActions = newBackgroundActions.map((action) => {
      const duration = action.end - action.start;
      const newAction = {
        ...action,
        start: currentTime,
        end: currentTime + duration,
      };
      currentTime += duration;
      return newAction;
    });

    // Update the background row with new actions
    const updatedBackgroundRow = {
      ...backgroundRow,
      actions: updatedBackgroundActions,
    };

    // Update all other rows to match the new scene order
    const otherRows = prevData.filter((row) => row.id !== 'background-row');
    const updatedOtherRows = otherRows.map((row) => {
      // Get all actions in this row
      const updatedActions = row.actions.map((action) => {
        // Get scene ID from the action's data
        const sceneId = action.data?.sceneId;
        if (!sceneId) return action;

        // Find the new position of this scene
        const newSceneIndex = orderedScenes.findIndex(
          (scene) => scene.id === sceneId
        );
        if (newSceneIndex === -1) return action;

        // Get the new scene's time range
        const newSceneAction = updatedBackgroundActions[newSceneIndex];

        // Update the action's time range to match the new scene
        return {
          ...action,
          start: newSceneAction.start,
          end: newSceneAction.end,
        };
      });

      return {
        ...row,
        actions: updatedActions,
      };
    });

    // Combine the updated rows
    return [updatedBackgroundRow, ...updatedOtherRows];
  };

  const handleSceneDrag = (draggedIndex: number, droppedIndex: number) => {
    // Update scenes order
    const newScenes = [...scenes];
    const [draggedScene] = newScenes.splice(draggedIndex, 1);
    newScenes.splice(droppedIndex, 0, draggedScene);
    setScenes(newScenes);

    // Update timeline data
    const newData = reorderTimelineForSceneOrder(
      timelineData,
      newScenes,
      draggedIndex,
      droppedIndex
    );
    setTimelineData(newData);

    // Persist the settled order (trailing-debounced: hover fires
    // continuously during a drag, so only the last call in the burst sends
    // the publish + PATCH). Without this the reorder is local-only and lost
    // on reload / invisible to collaborators.
    debouncedSceneOrderRef.current = {
      scenes: newScenes,
      actionData: extractTimelineActions(newData),
    };
    debouncedPersistSceneOrder(videoId);
  };

  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Derive scene durations from timeline rows and persist them
  // (trailing-debounced). The timeline itself is persisted live by the
  // engine's onChange; this covers only the derived scene metadata.
  const queueSceneDurationPersist = (newData: CusTomTimelineRow[]) => {
    const backgroundRow = newData.find((row) => row.id === 'background-row');
    setScenes((prevScenes) => {
      const updatedScenes = prevScenes.map((scene) => {
        const sceneAction = backgroundRow?.actions.find(
          (action) => action.id === `background-action-${scene.id}`
        );
        return {
          ...scene,
          duration: sceneAction ? sceneAction.end - sceneAction.start : 10,
        };
      });
      debouncedSceneOrderRef.current = {
        scenes: updatedScenes,
        actionData: null,
      };
      debouncedPersistSceneOrder(videoId);
      return updatedScenes;
    });
  };

  const handleTimelineChange = (
    data:
      | CusTomTimelineRow[]
      | ((prev: CusTomTimelineRow[]) => CusTomTimelineRow[])
  ) => {
    // TimelineEditor's sync effects AND the engine wrapper both pass an
    // updater function rather than an array — a function has no .find, so
    // resolve it against latest state first (this crash: data.find is not
    // a function on every load with synced audio/texts/backgrounds).
    if (typeof data === 'function') {
      setTimelineData((prevData) => {
        const newData = (
          data as (prev: CusTomTimelineRow[]) => CusTomTimelineRow[]
        )(prevData);
        queueSceneDurationPersist(newData);
        return newData;
      });
    } else {
      // Derive durations from the NEW rows, not the (stale) timelineData
      // closure — the engine calls this with already-updated data, so
      // reading state here would persist one-step-behind durations.
      setTimelineData(data);
      queueSceneDurationPersist(data);
    }
  };

  const handleBackgroundSelect = (imageData: string | null): void => {
    if (!selectedSceneId) {
      toast.error(toastMessage('scene.selectFirst'));
      return;
    }

    // Get the current timeline time
    const currentTime = timelineState.current?.getTime() || 0;

    // Find the scene's time range from the background row
    const backgroundRow = timelineData.find(
      (row) => row.id === 'background-row'
    );
    const sceneAction = backgroundRow?.actions.find(
      (action) => action.id === `background-action-${selectedSceneId}`
    );

    if (!sceneAction) {
      toast.error(toastMessage('scene.notFound'));
      return;
    }

    // Only update the background image if we're within the scene's time range
    if (currentTime >= sceneAction.start && currentTime < sceneAction.end) {
      setBackgroundImage(imageData);
    }

    setScenes((prevScenes) => {
      const newScenes = prevScenes.map((scene) => {
        if (scene.id === selectedSceneId) {
          const updatedScene = {
            ...scene,
            backgroundImage: imageData || undefined,
          };
          return updatedScene;
        }
        return scene;
      });

      channel.publish('handleScenesUpdate', newScenes);
      if (videoId) {
        videoService.patchVideo(videoId, { scenes: newScenes });
      }
      return newScenes;
    });

    // Update the background in the timeline
    setTimelineData((prevData) => {
      const newData = prevData.map((row) => {
        if (row.id === 'background-row') {
          return {
            ...row,
            actions: row.actions.map((action) =>
              action.id === `background-action-${selectedSceneId}`
                ? {
                    ...action,
                    data: {
                      ...action.data,
                      imageData: imageData,
                    },
                  }
                : action
            ),
          };
        }
        return row;
      });
      const actionData = extractTimelineActions(newData);
      channel.publish('handleTimelineEdit', actionData);

      if (videoId) {
        videoService.patchVideo(videoId, { timeline: actionData });
      }
      return newData;
    });

    // Update layers
    setLayers((prevLayers: Layer[]) => {
      const existingLayerIndex = prevLayers.findIndex(
        (layer) => layer.name === 'Background Layer'
      );

      if (existingLayerIndex !== -1) {
        // Update the existing background layer's imageUrl
        const updatedLayers = [...prevLayers];
        updatedLayers[existingLayerIndex] = {
          ...updatedLayers[existingLayerIndex],
          imageUrl: imageData,
        };
        return updatedLayers;
      } else {
        // Add a new background layer
        const newLayers = [
          {
            id: crypto.randomUUID(),
            name: 'Background Layer',
            type: AssetType.BACKGROUND,
            visible: true,
            locked: false,
            imageUrl: imageData,
          },
          ...prevLayers,
        ];
        return newLayers;
      }
    });

    // Publish the background change
    if (videoId) {
      videoService.patchVideo(videoId, { backgroundImage: imageData });
    }
    channel.publish('handleBackgroundChange', imageData);
  };

  return (
    <div className="homepage">
      <Header
        onPublish={handlePublish}
        isPublishWorking={isPublishing || publishJob.status === 'working'}
        isEditorReady={editorReady}
        title={title}
        setTitle={setTitle}
        channel={channel}
        ownedBy={ownedBy}
        sharedWith={sharedWith}
        setSharedWith={setSharedWith}
      />
      <aside className="toolbar">
        <ToolBar
          onBackgroundSelect={handleBackgroundSelect}
          onAnimationSelect={handleAnimationSelect}
          onEffectSelect={handleEffectSelect}
          onUploadedAssetSelect={handleUploadedAssetSelect}
          onTextSelect={handleTextSelect}
          layers={layers}
          onLayerDrag={handleLayerDrag}
          handleLayerLock={handleLayerLock}
          selectedMenu={selectedMenu}
          charactersRef={charactersRef}
          textRef={textRef}
          onMenuClick={handleMenuClick}
          onMenuClose={handleMenuCloseClick}
          onAudioSelect={handleAudioSelect}
          onUpdateCharacter={updateCharacterProperties}
          onUpdateText={updateTextProperties}
          onUpdateIsRecording={setIsRecording}
          onUpdateRecordingStartTime={setRecordingStartTime}
          isRecording={isRecording}
          selectedAssetOnCanvas={selectedAssetOnCanvas}
          navMenuRef={navMenuRef}
          onAiAudioRequest={handleAiAudioRequest}
          onAiBackgroundRequest={handleAiBackgroundRequest}
          onSaveAndCanvasImportBackground={handleSaveAndCanvasImportBackground}
          onSaveAndCanvasImportAudio={handleSaveAndCanvasImportAudio}
          lastSelectedCharacterIndex={lastSelectedCharacterIndex}
          videoId={videoId}
          videoSas={activeVideoSas}
          audioFiles={audioFiles}
          setAudioFiles={setAudioFiles}
          riggedCharactersRef={riggedCharactersRef}
          onUpdateRiggedCharacter={handleUpdateRiggedCharacter}
          onOpenCharacterLibrary={() => setIsCharacterLibraryOpen(true)}
          onOpenCharacterVideoGenerator={() => {
            setCharacterVideoMode('character');
            setIsCharacterVideoGeneratorOpen(true);
          }}
          onOpenLiveActionGenerator={() => {
            setCharacterVideoMode('liveAction');
            setIsCharacterVideoGeneratorOpen(true);
          }}
          onOpenEnvironmentLibrary={() => setIsEnvironmentLibraryOpen(true)}
        />
      </aside>
      {loading ? (
        <div className="loader-container">
          <ClipLoader size={50} color="#36CEDC" />
        </div>
      ) : (
        <>
          <main className="main-content">
            <div
              className={`asset-menu ${assetMenuVisible ? 'visible' : 'hidden'}`}
              style={{ top: `${floatingMenuTop}px` }}
            >
              <AssetMenu
                visible={assetMenuVisible}
                selectedAssetOnCanvas={selectedAssetOnCanvas}
                updateTextProperties={updateTextProperties}
                selectedText={
                  selectedAssetOnCanvas
                    ? selectedTexts.find(
                        (text) => text.id === selectedAssetOnCanvas.id
                      )
                    : undefined
                }
                onClose={() => setAssetMenuVisible(false)}
                onMenuClick={handleMenuClick}
                onDeleteAsset={handleDeleteAsset}
                assetMenuRef={assetmenuRef}
                navMenuRef={navMenuRef}
              />
            </div>
            <div className="canvas-stage" ref={canvasWrapperRef}>
              <PixiCanvas
                width={canvasWidth}
                height={canvasHeight}
                backgroundColor={0xffffff}
                image={backgroundImage}
                animation={selectedAnimation}
                selectedTexts={selectedTexts}
                effect={selectedEffect}
                assets={selectedUploadedAssets}
                layers={layers}
                charactersRef={charactersRef}
                effectsSpriteRef={effectsSpriteRef}
                textRef={textRef}
                onUpdateCharacter={updateCharacterProperties}
                onUpdateAsset={updateAssetProperties}
                onUpdateText={updateTextProperties}
                onAssetSelection={handleAssetSelectionOnCanvas}
                isRecording={isRecording}
                recordingStartTime={recordingStartTime}
                selectedMenu={selectedMenu}
                isCommentsOpen={isCommentsOpen}
                comments={comments}
                handleAddCommentDrawingPoint={handleAddCommentDrawingPoint}
                drawingRef={drawingRef}
                commentContainerRef={commentContainerRef}
                appRef={appRef}
                setLastSelectedCharacterIndex={setLastSelectedCharacterIndex}
                backgroundImageRef={backgroundImageRef}
                riggedCharacters={riggedCharacters}
                riggedCharactersRef={riggedCharactersRef}
                onUpdateRiggedCharacter={handleUpdateRiggedCharacter}
                timelineState={timelineState}
                onBackgroundReady={handleInitialCanvasReady}
              />
            </div>
            <div className="comments">
              <Comments
                isOpen={isCommentsOpen}
                onClose={() => setIsCommentsOpen(false)}
                comments={comments}
                onAddComment={handleAddComment}
                selectedCommentId={selectedCommentId}
              />
            </div>

            <footer className="timeline">
              <Timeline
                selectedAudio={selectedAudio}
                selectedEffect={selectedEffect}
                selectedAnimation={selectedAnimation}
                selectedTexts={selectedTexts}
                setIsCommentsOpen={setIsCommentsOpen}
                comments={comments}
                characterRef={charactersRef}
                effectsSpriteRef={effectsSpriteRef}
                textRefs={textRef}
                timelineState={timelineState}
                onAudioRecordingComplete={handleAudioRecordingComplete}
                data={timelineData}
                setData={handleTimelineChange}
                channel={channel}
                setSelectedCommentId={setSelectedCommentId}
                selectedBackgrounds={selectedBackgrounds}
                setBackgroundImage={setBackgroundImage}
                onDeleteTimelineAction={handleDeleteTimelineAction}
                onDeleteScene={handleDeleteScene}
              />
            </footer>
          </main>
          <aside className="scene-menu-container">
            <SceneMenu
              scenes={scenes}
              videoSas={activeVideoSas}
              onAddScene={handleAddScene}
              onSelectScene={handleSelectScene}
              selectedSceneId={selectedSceneId}
              onUpdateSceneName={handleUpdateSceneName}
              onDeleteScene={handleDeleteScene}
              onDuplicateScene={handleDuplicateScene}
              onTransitionScene={handleTransitionScene}
              onSceneDrag={handleSceneDrag}
              formatDuration={formatDuration}
            />
          </aside>
          {!initialCanvasReady && (
            <div className="loader-container canvas-boot-overlay">
              <ClipLoader size={50} color="#36CEDC" />
              <span className="canvas-boot-label">Preparing canvas…</span>
            </div>
          )}
        </>
      )}

      {/* Character Library Modal */}

      {isCharacterVideoGeneratorOpen && (
        <div
          className="character-library-overlay"
          onClick={requestCloseCharacterVideoGenerator}
        >
          <div
            className="character-library-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="character-library-header">
              <h2>
                {characterVideoMode === 'liveAction'
                  ? 'Live Action to Animation'
                  : 'Character Image to Video'}
              </h2>
              <button
                className="close-button"
                onClick={requestCloseCharacterVideoGenerator}
                aria-label="Close"
              >
                <Close />
              </button>
            </div>
            <div className="character-library-content">
              <CharacterVideoGenerator
                mode={characterVideoMode}
                videoId={videoId}
                videoSasToken={activeVideoSas?.token}
                onCancel={requestCloseCharacterVideoGenerator}
                onVideoGenerated={handleAddCharacterVideoToTimeline}
                onOpenCharacterStudio={
                  handleOpenCharacterStudioFromVideoGenerator
                }
                libraryRevision={characterLibraryRevision}
                environmentRevision={environmentLibraryRevision}
                onOpenEnvironmentLibrary={() =>
                  setIsEnvironmentLibraryOpen(true)
                }
                onUnsavedChange={setIsCharacterVideoUnsaved}
              />
            </div>
          </div>
        </div>
      )}

      {isPortraitPhone && !rotateDismissed && (
        <div
          className="rotate-prompt"
          role="dialog"
          aria-modal="true"
          aria-label="Rotate your phone to use the editor"
        >
          <div className="rotate-prompt-card">
            <span className="rotate-prompt-icon" aria-hidden="true">
              <svg
                viewBox="0 0 24 24"
                width="40"
                height="40"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="7" y="2.5" width="10" height="19" rx="2.5" />
                <path d="M3.5 8.5a8 8 0 0 1 8-5.5" />
                <path d="M3.5 8.5 2 5.8M3.5 8.5l2.9-.6" />
              </svg>
            </span>
            <h2>Rotate your phone</h2>
            <p>
              The canvas editor is designed for landscape on phones — you get
              a bigger canvas, usable scenes panel and timeline.
            </p>
            <button
              type="button"
              className="rotate-prompt-primary"
              onClick={() => setRotateDismissed(true)}
            >
              Continue anyway
            </button>
          </div>
        </div>
      )}

      <CharacterLibrary
        isOpen={isCharacterLibraryOpen}
        onClose={handleCloseCharacterLibrary}
        focusCharacterId={studioFocusCharacterId}
        onSelectCharacter={async (rig: CharacterRig, actionId?: string) => {
          // The rigged-character canvas flow is part of the rigging feature.
          if (!isCharacterRiggingEnabled()) return;
          // The stored imageUrl may be a relative blob path in the user's
          // storage directory; resolve it with the user SAS token so the
          // canvas can render the private blob.
          const resolvedImageUrl = await resolveCharacterImageUrl(rig.imageUrl);

          // Create a new instance of the rigged character. The rig used on the
          // canvas carries the signed URL while the stored character document
          // keeps the raw relative path so it can be re-signed later.
          const rigForCanvas = {
            ...rig,
            imageUrl: resolvedImageUrl || rig.imageUrl,
            assets: rig.assets?.map((asset) => ({
              ...asset,
              url:
                asset.url === rig.imageUrl
                  ? resolvedImageUrl || asset.url
                  : asset.url,
            })),
          };

          const newRiggedChar = {
            id: `rig-${Date.now()}`,
            rig: rigForCanvas,
            position: { x: canvasWidth / 2, y: canvasHeight / 2 },
            scale: 0.5,
            actionId: actionId,
          };

          setRiggedCharacters((prev) => [...prev, newRiggedChar]);
          handleCloseCharacterLibrary();

          // Timeline Integration
          if (!selectedSceneId) return;

          const backgroundRow = timelineData.find(
            (row) => row.id === 'background-row'
          );
          const sceneAction = backgroundRow?.actions.find(
            (action) => action.id === `background-action-${selectedSceneId}`
          );

          if (!sceneAction) return;

          // Find existing row that can accommodate this animation
          const existingRow = timelineData.find((row) => {
            if (row.id === 'background-row') return false;
            const hasOverlappingAction = row.actions.some((action) => {
              return (
                (action.start >= sceneAction.start &&
                  action.start < sceneAction.end) ||
                (action.end > sceneAction.start &&
                  action.end <= sceneAction.end) ||
                (action.start <= sceneAction.start &&
                  action.end >= sceneAction.end)
              );
            });
            return !hasOverlappingAction;
          });

          const newAction = {
            id: `rig-action-${newRiggedChar.id}`,
            start: sceneAction.start,
            end: sceneAction.end,
            effectId: 'effect5',
            data: {
              src: newRiggedChar.id,
              name: 'Rigged Character',
              ref: riggedCharactersRef,
              sceneId: selectedSceneId,
            },
          };

          setTimelineData((prevData) => {
            const bgRow = prevData.find((row) => row.id === 'background-row');
            if (!bgRow) return prevData;

            const newData = [bgRow];

            if (existingRow) {
              const updatedRow = {
                ...existingRow,
                actions: [...existingRow.actions, newAction],
              };
              const otherRows = prevData.filter(
                (row) =>
                  row.id !== 'background-row' && row.id !== existingRow.id
              );
              newData.push(...otherRows, updatedRow);
            } else {
              const newRow: CusTomTimelineRow = {
                id: newRiggedChar.id,
                name: 'Rigged Character',
                start: sceneAction.start,
                end: sceneAction.end,
                actions: [newAction],
              };
              const otherRows = prevData.filter(
                (row) => row.id !== 'background-row'
              );
              newData.push(...otherRows, newRow);
            }
            return newData;
          });

          setLayers((prev) => [
            ...prev,
            {
              id: newRiggedChar.id,
              name: 'Rigged Character',
              type: AssetType.RIGGED_CHARACTER,
              visible: true,
              locked: false,
              imageUrl: newRiggedChar.rig.imageUrl,
            },
          ]);
        }}
      />

      <EnvironmentLibrary
        isOpen={isEnvironmentLibraryOpen}
        onClose={handleCloseEnvironmentLibrary}
        onLibraryChange={() =>
          setEnvironmentLibraryRevision((revision) => revision + 1)
        }
      />
    </div>
  );
};

export default HomePage;
